<template>
  <div class="order-detail">
    <van-nav-bar :fixed="true" z-index="9" :title="title" :border="false" left-arrow @click-left="onClickLeft"/>
    <van-pull-refresh
      class="detail-pull-refresh"
      v-model="refreshing"
      :pulling-text="$t('loading')"
      :loosing-text="$t('loading')"
      :loading-text="$t('loading')"
      success-text=""
      :success-duration="0"
      @refresh="onRefresh"
    >
      <div v-if="detailLoading" class="order-state">{{ $t('loading') }}</div>
      <div v-else-if="detailLoadError" class="order-state order-state--error">
        <div>{{ $t('systemError') }}</div>
        <div class="btn-primary btn-normal" @click="getOrderDetail">{{ $t('retry') }}</div>
        <div class="order-state__back" @click="onClickLeft">{{ $t('back') }}</div>
      </div>
      <template v-if="showData">
        <div class="android-order-detail">
        <section class="android-order-status-card" :class="statusClass">
          <div class="android-order-status-card__top">
            <div class="android-order-status-card__icon" aria-hidden="true">
              <van-icon :name="statusIconName" />
            </div>
            <div class="android-order-status-card__copy">
              <div class="android-order-status-card__title">{{ statusCardTitle }}</div>
            </div>
            <span v-if="statusCardBadge" class="android-order-status-card__badge">{{ statusCardBadge }}</span>
          </div>
          <div class="android-order-status-card__body" v-if="statusCardDescription || statusCardTime || showCloseReasonStatusRow || showCodShippingFeePayAction || showCodShippingFeePaidStatus || showStorePayAtStoreAction || showStorePayAtStorePaidStatus">
            <template v-if="isClosedOrCancelled">
              <div v-if="statusCardDescription" class="android-order-status-card__list-item"><span class="android-order-status-card__bullet"></span><span class="android-order-status-card__list-text">{{ statusCardDescription }}</span></div>
              <div v-if="statusCardTime" class="android-order-status-card__list-item"><span class="android-order-status-card__bullet"></span><span class="android-order-status-card__list-text">{{ $t('orderPage.cancelledOnFormat', { time: statusCardTime }) }}</span></div>
              <div v-if="showCloseReasonStatusRow" class="android-order-status-card__list-item"><span class="android-order-status-card__bullet"></span><span class="android-order-status-card__list-text">{{ $t('orderPage.closeReason') }}: {{ closeReasonText }}</span></div>
            </template>
            <template v-else>
              <div v-if="statusCardDescription" class="android-order-status-card__description">{{ statusCardDescription }}</div>
              <div v-if="statusCardTime" class="android-order-status-card__time">
                <van-icon name="clock-o" />
                <span>{{ statusCardTimeLabel }}</span>
              </div>
              <div v-if="showCodShippingFeePaidStatus" class="android-order-status-card__cod-paid">
                <span>{{ codShippingFeePaidStatusLabel }}</span>
                <strong>{{ $t('paid') }}</strong>
              </div>
              <button
                v-if="showCodShippingFeePayAction"
                type="button"
                class="android-order-status-card__cod-pay"
                @click="goCodShippingFeeCheckout"
              >
                {{ codShippingFeePayActionText }}
              </button>
              <button v-if="showStorePayAtStoreAction" type="button" class="android-order-status-card__store-pay" @click="goStorePaymentCheckout">
                {{ storePayAtStoreActionText }}
              </button>
              <div v-if="showStorePayAtStorePaidStatus" class="android-order-status-card__store-pay android-order-status-card__store-pay--paid">
                <span>{{ storePayAtStorePaidStatusText }}</span>
              </div>
            </template>
          </div>
          <div class="android-order-status-card__link" v-if="showLogisticsCard" @click="onLogisticsClick">
            <span>{{ logisticsStatusText }}</span><van-icon name="arrow" />
          </div>
          <div class="android-order-status-card__pending" v-if="showPaymentCountdown">
            <div class="android-order-status-card__pending-desc">{{ $t('orderPage.pendingPaymentTimeLimitDesc') }}</div>
            <div class="android-order-status-card__pending-countdown">
              <van-icon name="clock-o" />
              <span>{{ paymentCountdownText }}</span>
            </div>
          </div>
        </section>

        <section v-if="showCancelStatusCard" class="android-order-cancel-card" :class="cancelStatusCardClass">
          <div class="android-order-cancel-card__icon"><van-icon :name="cancelStatusIcon" /></div>
          <div class="android-order-cancel-card__body">
            <div class="android-order-cancel-card__title">
              <span>{{ cancelStatusTitle }}</span>
              <span v-if="cancelStatusInlineStatus" class="android-order-cancel-card__inline-status">{{ cancelStatusInlineStatus }}</span>
            </div>
            <div class="android-order-cancel-card__list" v-if="cancelStatusRows.length">
              <div v-for="row in cancelStatusRows" :key="row.key" class="android-order-cancel-card__list-item">
                <span class="android-order-cancel-card__bullet"></span>
                <span class="android-order-cancel-card__list-text">{{ row.text }}</span>
              </div>
            </div>
            <div v-if="showWithdrawCancelBtn" class="android-order-cancel-card__action">
              <button class="android-order-text-button" @click="onWithdrawCancel">{{ $t('orderList.cancelStatus.withdrawRequest') }}</button>
            </div>
          </div>
        </section>

        <section v-if="isStore" class="android-order-section order-store-card">
          <button type="button" class="android-order-section__header" :aria-expanded="expandedSections.store" aria-controls="order-store-content" @click="toggleSection('store')">
            <span class="android-order-section__heading"><van-icon name="shop-o" /><span>{{ $t('orderPage.storeDetailsTitle') }}</span></span>
            <van-icon name="arrow-down" class="android-order-fold__arrow" :class="{ 'android-order-fold__arrow--expanded': expandedSections.store }" />
          </button>
          <div id="order-store-content" class="android-order-fold__collapse" :class="{ 'android-order-fold__collapse--expanded': expandedSections.store }" :aria-hidden="!expandedSections.store">
            <div class="android-order-fold__inner">
              <div class="android-order-section__body" role="region">
                <div v-if="storeLoading" class="order-store-card__state">{{ $t('loading') }}</div>
                <div v-else-if="pickupStore" class="android-order-address">
                  <div class="android-order-address__name order-store-card__name">{{ pickupStore.name || '--' }}</div>
                  <div class="android-order-address__detail android-order-address__detail--expanded">{{ pickupStore.address || '--' }}</div>
                  <a v-if="pickupStore.phone" class="android-order-address__phone order-store-card__phone" :href="`tel:${pickupStore.phone}`" @click.stop>{{ formatIndonesiaPhone(pickupStore.phone) }}</a>
                  <div v-if="pickupStore.businessHour" class="order-store-card__hours">{{ $t('orderPage.storeHours') }}: {{ formatStoreBusinessHour(pickupStore.businessHour) }}</div>
                </div>
                <div v-else class="order-store-card__state order-store-card__state--error">
                  <span>{{ $t('orderPage.storeLoadFailed') }}</span>
                  <button v-if="pickupStoreId" type="button" @click.stop="loadPickupStore">{{ $t('retry') }}</button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="android-order-section">
          <button type="button" class="android-order-section__header" :aria-expanded="expandedSections.address" aria-controls="order-address-content" @click="toggleSection('address')">
            <span class="android-order-section__heading"><android-order-icon name="address" :size="36" /><span>{{ $t('orderPage.deliveryAddress') }}</span></span>
            <van-icon name="arrow-down" class="android-order-fold__arrow" :class="{ 'android-order-fold__arrow--expanded': expandedSections.address }" />
          </button>
          <div id="order-address-content" class="android-order-fold__collapse" :class="{ 'android-order-fold__collapse--expanded': expandedSections.address }" :aria-hidden="!expandedSections.address">
            <div class="android-order-fold__inner">
              <div class="android-order-section__body" role="region">
                <div v-if="address" class="android-order-address">
                  <div class="android-order-address__name">{{ address.recipientName || '--' }}</div>
                  <div class="android-order-address__phone">{{ formatIndonesiaPhone(address.recipientPhone) || '--' }}</div>
                  <div class="android-order-address__detail android-order-address__detail--expanded">{{ addressDetail }}</div>
                </div>
                <div v-else class="android-order-muted">{{ $t('orderPage.addressTip') }}</div>
              </div>
            </div>
          </div>
        </section>

        <section v-if="showData.emergencyContactList && showData.emergencyContactList.length" class="android-order-section">
          <button type="button" class="android-order-section__header" :aria-expanded="expandedSections.contacts" aria-controls="order-contacts-content" @click="toggleSection('contacts')">
            <span class="android-order-section__heading"><android-order-icon name="contact" :size="36" /><span>{{ $t('orderPage.referenceContacts') }}</span></span>
            <van-icon name="arrow-down" class="android-order-fold__arrow" :class="{ 'android-order-fold__arrow--expanded': expandedSections.contacts }" />
          </button>
          <div id="order-contacts-content" class="android-order-fold__collapse" :class="{ 'android-order-fold__collapse--expanded': expandedSections.contacts }" :aria-hidden="!expandedSections.contacts">
            <div class="android-order-fold__inner">
              <div class="android-order-section__summary">{{ $tc('orderPage.contactsAdded', showData.emergencyContactList.length, { count: showData.emergencyContactList.length }) }}</div>
              <div class="android-order-section__body" role="region">
                <div v-for="(contact, index) in showData.emergencyContactList" :key="contact.phone || contact.mobile || contact.name" class="android-order-contact">
                  <div class="android-order-contact__identity">
                    <span class="android-order-contact__name">{{ contact.name || '--' }}</span>
                    <span v-if="relationshipText(contact)" class="android-order-contact__relationship"> ({{ relationshipText(contact) }})</span>
                  </div>
                  <div class="android-order-contact__phone">{{ formatIndonesiaPhone(contact.phone || contact.mobile) || '--' }}</div>
                  <div v-if="index < showData.emergencyContactList.length - 1" class="android-order-contact__divider"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="android-order-section">
          <div class="android-order-section__header android-order-section__header--static">
            <span class="android-order-section__heading"><android-order-icon name="iphone" :size="36" /><span>{{ $t('orderPage.product') }}</span></span>
          </div>
          <div class="android-order-section__body" role="region">
            <div class="android-order-number">
              <span>{{ $t('orderList.orderNo', { id: showData.orderId }) }}</span>
              <button class="android-order-copy" @click="copyOrderNumber" :aria-label="$t('orderPage.copyOrderNumber')"><android-order-icon name="copy" :size="36" /></button>
            </div>
            <div class="android-order-product">
              <div class="android-order-product__image" v-lazy:background-image="showData.imageUrl"></div>
              <div class="android-order-product__info">
                <div class="android-order-product__name">{{ showData.goodsName }}</div>
                <div class="android-order-muted">{{ showData.skuStr }}</div>
              </div>
            </div>
          </div>
        </section>

        <section class="android-order-section">
          <div class="android-order-section__header android-order-section__header--static">
            <span class="android-order-section__heading"><android-order-icon name="date" :size="36" /><span>{{ $t('orderPage.rentalDates') }}</span></span>
          </div>
          <div class="android-order-section__body" role="region">
            <div class="android-order-date-grid">
              <div class="android-order-date-block"><span>{{ $t('orderPage.rentalStartDateDetail') }}</span><strong>{{ showData.rentalStartDate | rentalDateFormat }}</strong><small v-if="showEstimatedRentalDateLabel">{{ $t('orderPage.rentalDateEstimated') }}</small></div>
              <div class="android-order-date-block"><span>{{ $t('orderPage.returnDateDetail') }}</span><strong>{{ showData.rentalEndDate | rentalDateFormat }}</strong><small v-if="showEstimatedRentalDateLabel">{{ $t('orderPage.rentalDateEstimated') }}</small></div>
            </div>
            <div class="android-order-info-note">
              <span class="android-order-info-note__icon" aria-hidden="true">i</span>
              <span class="android-order-info-note__text">{{ $t('orderPage.rentalStartAdjusted') }}</span>
            </div>
          </div>
        </section>

        <section v-if="showCodShippingFeeNotice" class="android-order-note-card android-order-note-card--cod android-order-note-card--cod-shipping-fee">
          <span class="android-order-note-card__icon" aria-hidden="true">i</span>
          <div class="android-order-note-card__content">
            <div class="android-order-note-card__title">{{ $t('orderPage.codShippingFee') }}</div>
            <div class="android-order-note-card__text">{{ codShippingFeeNoticeDesc }}</div>
          </div>
        </section>

        <section v-if="shouldShowCodPaymentReminder" class="android-order-note-card android-order-note-card--cod android-order-note-card--cod-reminder">
          <span class="android-order-note-card__icon" aria-hidden="true">i</span>
          <div class="android-order-note-card__content">
            <div class="android-order-note-card__title">{{ $t('orderPage.codPaymentReminder') }}</div>
            <div class="android-order-note-card__text">{{ codReminderIntroText }}</div>
            <div class="android-order-note-card__cod-row">
              <span class="android-order-note-card__currency">{{ priceUint }}</span>
              <div class="android-order-note-card__cod-desc">
                {{ codReminderMessageParts.prefix }}<span class="android-order-note-card__amount">{{ codReminderMessageParts.amount }}</span>{{ codReminderMessageParts.suffix }}
              </div>
            </div>
          </div>
        </section>

        <section v-if="showStorePaymentReminder" class="android-order-note-card android-order-note-card--store-reminder">
          <span class="android-order-note-card__icon" aria-hidden="true">i</span>
          <div class="android-order-note-card__content">
            <div class="android-order-note-card__title">{{ $t(showStorePayAtStorePaidStatus ? 'orderPage.storePickupReminderTitle' : 'orderPage.storePaymentReminder') }}</div>
            <div class="android-order-note-card__text">{{ $t(showStorePayAtStorePaidStatus ? 'orderPage.storePickupReminderDesc' : 'orderPage.storePaymentReminderDesc') }}</div>
          </div>
        </section>

        <section class="android-order-section">
          <div class="android-order-section__header android-order-section__header--static">
            <span class="android-order-section__heading"><android-order-icon name="currency" :size="36" /><span>{{ $t('orderPage.pricePlan') }}</span></span>
          </div>
          <div class="android-order-section__body" role="region">
            <div class="android-order-price-row"><span>{{ $t('orderPage.firstRentalPayment') }}</span><span>{{ priceUint }}{{ showData.firstRentalAmount | numberFormat }}</span></div>
            <div class="android-order-price-row"><span>{{ periodicRentLabel }}</span><span>{{ priceUint }}{{ showData.planPrice | numberFormat }}/{{ periodicRentUnit }}</span></div>
            <div class="android-order-price-row"><span>{{ $t('orderPage.rentalTerm') }}</span><span>{{ rentalTermText }}</span></div>
            <div class="android-order-price-row"><span>{{ $t('orderPage.deposit') }} <small>{{ $t('orderPage.refundableParenthetical') }}</small></span><span>{{ priceUint }}{{ showData.depositAmount | numberFormat }}</span></div>
            <div v-if="isCod" class="android-order-price-row"><span>{{ $t('orderPage.codShippingFee') }}</span><span>{{ priceUint }}{{ codShippingFeeAmount | numberFormat }}</span></div>
            <div v-if="!isClosedOrCancelled" class="android-order-divider"></div>
            <template v-if="!isClosedOrCancelled && isCod">
              <div class="android-order-price-row android-order-price-row--total android-order-price-row--cod-total"><span>{{ codApprovalAmountLabel }}</span><strong>{{ priceUint }}{{ dueAfterApprovalAmount | numberFormat }}</strong></div>
              <div class="android-order-price-row android-order-price-row--total android-order-price-row--cod-total"><span>{{ $t('orderPage.amountDueOnDelivery') }}</span><strong>{{ priceUint }}{{ dueOnDeliveryAmount | numberFormat }}</strong></div>
            </template>
            <div v-else-if="!isClosedOrCancelled" class="android-order-price-row android-order-price-row--total"><span>{{ pricePlanAmountLabel }}</span><strong>{{ priceUint }}{{ effectivePaymentAmount | numberFormat }}</strong></div>
            <button v-if="canOpenRepayPlan" class="android-order-link-row" @click="goRepayPlan"><van-icon class="android-order-link-row__icon" name="orders-o" /><span>{{ $t('planDetail.title') }}</span><van-icon name="arrow" /></button>
            <div v-if="showPricePlanNotice" class="android-order-price-notices">
              <div v-if="showCancelledOrderNotices" class="android-order-price-notice android-order-price-notice--info">
                <android-order-icon name="protect" :size="36" />
                <div><strong>{{ $t('orderPage.cancelledOrderNoPaymentTitle') }}</strong><span>{{ $t('orderPage.cancelledOrderNoPaymentDesc') }}</span></div>
              </div>
              <div v-if="showCancelledOrderNotices" class="android-order-price-notice android-order-price-notice--warning">
                <android-order-icon name="protect" :size="36" />
                <div><strong>{{ $t('orderPage.cancelledOrderNeedHelpTitle') }}</strong><span>{{ $t('orderPage.cancelledOrderNeedHelpDesc') }}</span></div>
              </div>
              <div v-else-if="showRentalPeriodOngoingNotice" class="android-order-price-notice android-order-price-notice--info">
                <android-order-icon name="protect" :size="36" />
                <div><strong>{{ $t('orderPage.rentalPeriodOngoingNoticeTitle') }}</strong><span>{{ $t('orderPage.rentalPeriodOngoingNoticeDesc') }}</span></div>
              </div>
              <div v-else-if="showPaymentReceivedNotice" class="android-order-price-notice android-order-price-notice--info">
                <android-order-icon name="protect" :size="36" />
                <div><strong>{{ $t('orderPage.paymentReceivedNoticeTitle') }}</strong><span>{{ $t('orderPage.paymentReceivedNoticeDesc') }}</span></div>
              </div>
              <div v-else-if="showRemainingAmountNotice" class="android-order-price-notice android-order-price-notice--info">
                <android-order-icon name="protect" :size="36" />
                <div><strong>{{ $t('orderPage.remainingAmountNoticeTitle') }}</strong><span>{{ $t('orderPage.remainingAmountNoticeDesc') }}</span></div>
              </div>
            </div>
            <div v-if="showPricePlanCloseReason" class="android-order-price-close-reason">
              <div class="android-order-divider"></div>
              <div class="android-order-price-row"><span>{{ $t('orderPage.closeReason') }}</span><span class="android-order-price-close-reason__value">{{ closeReasonText }}</span></div>
            </div>
          </div>
        </section>

        <section class="android-order-note-card android-order-notes-card">
          <div class="android-order-notes-card__title"><android-order-icon name="protect" :size="36" /><span>{{ $t('orderPage.ownershipNoticeTitle') }}</span></div>
          <div class="android-order-notes-card__body">{{ $t('orderPage.ownershipNotice') }}</div>
        </section>

        <section class="android-order-note-card android-order-notes-card">
          <div class="android-order-notes-card__title"><android-order-icon name="note" :size="36" /><span>{{ $t('orderPage.importantNotesTitle') }}</span></div>
          <ul class="android-order-note-list">
            <li><span class="android-order-note-bullet" aria-hidden="true"></span><span>{{ $t('orderPage.importantNotes1') }}</span></li>
            <li><span class="android-order-note-bullet" aria-hidden="true"></span><span>{{ $t('orderPage.importantNotes2') }}</span></li>
            <li><span class="android-order-note-bullet" aria-hidden="true"></span><span>{{ $t('orderPage.importantNotes3') }}</span></li>
          </ul>
        </section>

        <button
          v-if="showBottomCancelOrderAction"
          type="button"
          class="android-order-bottom-cancel"
          @click="onCancelClick"
        >
          {{ $t('orderList.cancelTitle') }}
        </button>

      </div>
      <div v-if="false" class="order-detail__legacy">
      <!--  顶部状态    -->
      <div class="order-detail__status" v-if="isDetail && statusText" :class="statusClass">
        <div class="status-badge">
          {{ statusText }}
        </div>
        <div class="status-close-reason" v-if="showCloseReasonStatusRow" style="display: flex;gap: 5px;align-items: center;">
          <div class="status-close-reason__label">{{ $t('orderPage.closeReason') }}:</div>
          <div class="status-close-reason__value">{{ closeReasonText }}</div>
        </div>
        <div class="order-detail__logistics" v-if="isDetail && showLogisticsCard" @click="onLogisticsClick">
          <div class="logistics-card">
            <div class="logistics-card__left">
              <div>
                <span class="logistics-card__status">{{ logisticsStatusText }}</span>
              </div>
            </div>
            <div class="logistics-card__arrow">>>></div>
          </div>
        </div>
        <div class="order-detail__logistics" v-if="isDetail && showPaymentCountdown">
          <div class="logistics-card" style="flex-direction: column; align-items: center; gap: 4px;">
            <span style="font-size: 14px;"><span style="color: var(--text-color-black)">{{ $t('orderList.paymentDeadline.prefix') }}</span>{{ paymentDeadline }}</span>
            <span style="font-size: 14px;color: var(--text-color-black)">{{ $t('orderList.paymentDeadline.suffix') }}</span>
          </div>
        </div>
        <div class="order-detail__status-action" v-if="showPaymentAction">
          <div class="status-pay-btn" :class="{ 'status-pay-btn--disabled': isPaymentExpired }" @click="openPaymentPopup">{{ paymentActionText }}</div>
        </div>
      </div>
      <div v-if="isDetail && showCancelStatusCard" class="order-cancel-card" :class="cancelStatusCardClass">
        <div class="order-cancel-card__icon">
          <van-icon :name="cancelStatusIcon" />
        </div>
        <div class="order-cancel-card__body">
          <div class="order-cancel-card__header">
            <span class="order-cancel-card__title">{{ cancelStatusTitle }}</span>
            <span v-if="showCancelStatusTime" class="order-cancel-card__time">{{ orderDetail.cancelApplyTime | rentalDateTimeFormat }}</span>
          </div>
          <div class="order-cancel-card__subtitle" v-if="cancelStatusSubtitle">{{ cancelStatusSubtitle }}</div>
          <div class="order-cancel-card__desc" v-if="cancelStatusDesc">{{ cancelStatusDesc }}</div>
          <div class="order-cancel-card__reason" v-if="cancelCloseReason">
            <div class="order-cancel-card__reason-label">{{ $t('orderPage.closeReason') }}:</div>
            <div class="order-cancel-card__reason-value">{{ cancelCloseReason }}</div>
          </div>
          <div class="order-cancel-card__action" v-if="showWithdrawCancelBtn" @click="onWithdrawCancel">
            {{ $t('orderList.cancelStatus.withdrawRequest') }}
          </div>
        </div>
      </div>
      <div class="order-detail__address">
        <div class="address-info flex align-center" v-if="address">
          <img class="address-info__img" src="@/assets/img/address.png" alt="">
          <div class="address-info__text">
            <div class="flex align-center">
              <div class="b-text t-break">{{ address.recipientName }}</div>
              <div>{{ address.recipientPhone }}</div>
            </div>
            <div class="mg-t-12 t-break">{{ addressDetail }}</div>
          </div>
        </div>
      </div>
      <div class="order-detail__contact mg-t-24" v-if="showData?.emergencyContactList?.length">
        <div class="contact-info flex align-center">
          <div>
            <Icon name="contact" class="icon-friends"/>
          </div>
          <div class="contact-info__text">
            <div class="contact-info__text-title">{{ $t('orderPage.contactTitle') }}</div>
            <div class="mg-t-12 contact-item" v-for="value in showData?.emergencyContactList" :key="value.phone || value.mobile || value.name">
              <span>{{ value.name }}</span>
              <span>{{ value.phone || value.mobile }}</span>
            </div>
            <div v-if="!showData?.emergencyContactList?.length">{{ $t('orderPage.noContacts') }}</div>
          </div>
        </div>
      </div>

      <div class="order-detail__goods mg-t-24">
        <div class="flex">
          <div class="order-detail__goods-info-img" v-lazy:background-image="showData.imageUrl"></div>
          <div class="mg-l-20 flex-1">
            <div class="order-detail__goods-info-name text-ellipsis">{{ showData.goodsName }}</div>
            <div class="order-detail__goods-info-sku">{{ showData.skuStr }}</div>
            <div class="order-detail__goods-info-sku">{{ $t('orderPage.firstRentalAmount') }}: {{ priceUint }}{{
                showData.firstRentalAmount |
                  numberFormat
              }}
            </div>
          </div>
        </div>
        <div class="flex justify-between align-center goods-plan">
          <div class="goods-plan__left">
            <img src="@/assets/img/order/startdate.png" alt="">
            <div class="desc">{{ $t('orderPage.rentalStartDate') }}</div>
            <div class="date">{{ showData.rentalStartDate | rentalDateFormat }}</div>
          </div>
          <div class="goods-plan__arrow"></div>
          <div class="goods-plan__right">
            <img src="@/assets/img/order/enddate.png" alt="">
            <div class="desc">{{ $t("orderPage.returnDate") }}</div>
            <div class="date">{{ showData.rentalEndDate | rentalDateFormat }}</div>
          </div>
        </div>
        <div class="goods-plan__tips">{{ $t('orderPage.planTips') }}</div>
      </div>

      <div v-if="isDetail && canOpenRepayPlan" class="repay-plan-entry" @click="goRepayPlan">
        <div>
          <div class="repay-plan-entry__title">{{ $t('planDetail.title') }}</div>
          <div class="repay-plan-entry__desc">{{ $t('planDetail.paymentRulesTitle') }}</div>
        </div>
        <van-icon name="arrow" />
      </div>

      <!--  补充信息    -->
      <div v-if="isDetail && showAdditionalInfoBanner" class="additional-info-banner" @click="goAdditionalInfo">
        <div class="additional-info-banner__icon">
          <van-icon name="records-o" />
        </div>
        <div class="additional-info-banner__body">
          <div class="additional-info-banner__title">{{ $t('orderPage.additionalInfoBannerTitle') }}</div>
          <div class="additional-info-banner__desc">{{ $t('orderPage.additionalInfoBannerDesc') }}</div>
        </div>
        <div class="additional-info-banner__arrow">></div>
      </div>

      <div class="cod-reminder-card" v-if="shouldShowCodPaymentReminder">
        <div class="cod-reminder-card__title">{{ $t('orderPage.codPaymentReminder') }}</div>
        <div class="cod-reminder-card__desc" v-html="codReminderDescHtml"></div>
      </div>

<!--金额展示部分-->
      <div class="order-detail__info mg-t-24">
        <div class="flex justify-between align-center mg-t-30">
          <div class="text-total" :class="{ 'cod-amount-title': isCod }">{{ isCod ? $t('orderPage.codAmountTitle') : $t('orderPage.total') }}</div>
          <div class="text-total org">{{ priceUint }}{{ showData.firstPayAmount | numberFormat }}</div>
        </div>
        <div class="flex justify-between align-center mg-t-20">
          <div class="text-left">{{ $t('orderPage.deposit') }}</div>
          <div class="text-right">{{ priceUint }}{{ showData.depositAmount | numberFormat }}</div>
        </div>
        <div class="flex justify-between align-center mg-t-20">
          <div class="text-left">{{ $t('orderPage.initialRent') }}</div>
          <div class="text-right">{{ priceUint }}{{ initialRentalPayment | numberFormat }}</div>
        </div>
        <div class="line-bottom mg-t-20"></div>

        <div class="flex justify-between align-center mg-t-20">
          <div class="text-left">{{ $t('orderPage.remainingRent') }}</div>
          <div class="text-right">{{ showData.remainingRent }} {{$t('orderPage.payments')}} * {{ priceUint }}{{ showData.planPrice | numberFormat }}
          </div>
        </div>
        <div class="flex justify-between align-center mg-t-20">
          <div class="text-left">{{ $t('orderPage.repaymentDate') }}</div>
          <div class="text-right">{{ showData.rentalCycle }}</div>
        </div>
        <div class="flex justify-between align-center mg-t-20">
          <div class="text-left">{{ $t('orderPage.totalRentalPayments') }}</div>
          <div class="text-right">{{ priceUint }}{{ showData.totalRentalPayments | numberFormat }}</div>
        </div>
      </div>

      <div class="order-detail__info2 mg-t-24" v-if="isDetail">
        <div class="flex justify-between align-center mg-t-20">
          <div class="text-left">{{ $t('orderPage.createdDate') }}</div>
          <div class="text-right">{{ showData.createTime | rentalDateTimeFormat }}</div>
        </div>
        <div class="flex justify-between align-center mg-t-20" v-if="showData.payTime || isCod">
          <div class="text-left">{{ isCod ? $t('orderPage.paymentDateCod') : $t('orderPage.paymenDate') }}</div>
          <div class="text-right">{{ paymentDateText }}</div>
        </div>
        <div class="flex justify-between align-center mg-t-20">
          <div class="text-left">{{ $t('orderPage.orderNumber') }}</div>
          <div class="text-right">{{ showData.orderId }}</div>
        </div>
        <div class="flex justify-between align-center mg-t-20" v-if="showCloseTimeRow">
          <div class="text-left">{{ $t('orderPage.closeDate') }}</div>
          <div class="text-right">{{ closeTimeText }}</div>
        </div>
        <div class="flex justify-between align-center mg-t-20" v-if="showCloseReasonInfoRow">
          <div class="text-left">{{ $t('orderPage.closeReason') }}</div>
          <div class="text-right text-right--multiline">{{ closeReasonText }}</div>
        </div>
      </div>

      <div class="order-detail__notes mg-t-20">
        <template>
          <div class="notes-title">{{$t('orderPage.ownershipNoticeTitle')}}</div>
          <div class="notes-content">{{$t('orderPage.ownershipNotice')}}</div>
          <div class="notes-title mg-t-30">{{$t('orderPage.importantNotesTitle')}}</div>
          <div class="notes-content">
            <p>• {{$t('orderPage.importantNotes1')}}</p>
            <p>• {{$t('orderPage.importantNotes2')}}</p>
            <p>• {{$t('orderPage.importantNotes3')}}</p>
          </div>
        </template>
      </div>

      <div class="cancel-btn-wrap" v-if="showCancelBtn">
        <div class="cancel-btn" @click="onCancelClick">{{ $t('orderList.cancelTitle') }}</div>
      </div>
        </div>
      </template>
    </van-pull-refresh>
    <div v-if="showData" class="android-order-footer" :class="{ 'android-order-footer--payment': showPaymentAction }">
      <template v-if="showPaymentAction">
        <div class="android-order-footer__row">
          <button class="android-order-footer__button android-order-footer__button--support" @click="openCustomerSupport" :aria-label="$t('orderPage.whatsapp')"><img src="@/assets/img/home-alignment/whatsapp.svg" :alt="$t('orderPage.whatsapp')"></button>
          <button class="android-order-footer__button android-order-footer__button--primary" @click="openPaymentPopup">{{ paymentActionText }}</button>
        </div>
      </template>
      <template v-else-if="showAwaitingShipmentAction">
        <button class="android-order-footer__button android-order-footer__button--support" @click="openCustomerSupport" :aria-label="$t('orderPage.whatsapp')"><img src="@/assets/img/home-alignment/whatsapp.svg" :alt="$t('orderPage.whatsapp')"></button>
        <button v-if="showStorePayAtStoreAction" class="android-order-footer__button android-order-footer__button--primary" @click="goStorePaymentCheckout">{{ $t('orderPage.pay') }}</button>
        <button v-else-if="showCodShippingFeePayAction" class="android-order-footer__button android-order-footer__button--primary" @click="goCodShippingFeeCheckout">{{ $t('orderPage.payCodShippingFee') }}</button>
        <button v-else class="android-order-footer__button android-order-footer__button--danger" @click="onCancelClick">{{ $t('orderList.cancelTitle') }}</button>
      </template>
      <template v-else>
        <button class="android-order-footer__button android-order-footer__button--support" @click="openCustomerSupport" :aria-label="$t('orderPage.whatsapp')"><img src="@/assets/img/home-alignment/whatsapp.svg" :alt="$t('orderPage.whatsapp')"></button>
        <button class="android-order-footer__button android-order-footer__button--secondary" @click="backToOrders">{{ $t('orderPage.backToOrders') }}</button>
      </template>
    </div>
    <ExpressProgress ref="deliveryProgress" :express="express"></ExpressProgress>
    <PaymentPopup ref="paymentPopup" @close="onPaymentPopupClose"/>
    <WithholdingAuthPopup v-model="showWithholdingAuthPopup" @authorize="onWithholdingAuthAuthorize"/>
    <Popup v-model="showPayUrlPopup" position="bottom">
      <div class="pay-url-popup">
        <div class="pay-url-popup__close" @click="showPayUrlPopup = false">
          <img src="@/assets/img/close.png" alt="">
        </div>
        <div class="pay-url-popup__title">{{ $t('orderPage.payUrlTitle') }}</div>
        <div class="pay-url-popup__desc">{{ $t('orderPage.payUrlDesc') }}</div>
        <div class="pay-url-popup__footer">
          <div class="btn-primary btn-normal" @click="goToPayUrl">{{ $t('orderPage.goToPay') }}</div>
          <div
            class="pay-url-popup__completed-btn"
            :class="{ 'pay-url-popup__completed-btn--disabled': checkingPaymentStatus }"
            @click="checkPayUrlPaymentStatus"
          >
            {{ $t('orderPage.paymentCompletedButton') }}
          </div>
        </div>
      </div>
    </Popup>
    <Popup ref="repayPlanPopup" v-model="repayPlanPopupVisible" position="bottom" round class="android-repay-plan-popup">
      <div class="android-repay-plan-sheet">
        <div class="android-repay-plan-sheet__header">
          <div class="android-repay-plan-sheet__title">{{ $t('orderPage.rentalPeriodPlan') }}</div>
          <van-icon name="cross" @click="repayPlanPopupVisible = false" />
        </div>
        <div v-if="canApplyReturnFromRepayPlan" class="android-repay-plan-sheet__return" @click="goTerminateFromRepayPlan">
          {{ repayPlanReturnActionText }}
        </div>
        <div class="android-repay-plan-sheet__body">
          <div class="android-repay-plan-sheet__notice">
            <span class="android-repay-plan-sheet__notice-icon"><android-order-icon name="date" :size="30" /></span>
            <div><strong>{{ $t('planDetail.repayPlanMonthlyTipTitle') }}</strong><span>{{ $t('planDetail.repayPlanMonthlyTip') }}</span></div>
          </div>
          <div v-if="repayPlanLoading" class="android-repay-plan-sheet__state">{{ $t('loading') }}</div>
          <div v-else-if="repayPlanError" class="android-repay-plan-sheet__state">
            <div>{{ $t('planDetail.repayPlanLoadFailed') }}</div>
            <button type="button" @click="loadRepayPlan">{{ $t('retry') }}</button>
          </div>
          <div v-else-if="!repayPlanItems.length" class="android-repay-plan-sheet__state">{{ $t('planDetail.repayPlanEmpty') }}</div>
          <div v-else class="android-repay-plan-sheet__items">
            <div v-for="item in repayPlanItems" :key="`order-plan-${item.period}`" class="android-repay-plan-item">
              <div class="android-repay-plan-item__top">
                <strong class="android-repay-plan-item__period">{{ repayPlanPeriodText(item.period) }}</strong>
                <span :class="`android-repay-plan-item__status android-repay-plan-item__status--${item.statusClass}`">
                  <android-order-icon v-if="item.statusClass === 'unpaid'" name="radioChecked" :size="28" />
                  <van-icon v-else :name="item.statusIconName" />
                  {{ item.statusText }}
                </span>
              </div>
              <div class="android-repay-plan-item__row"><span>{{ $t('orderPage.repayPlanDueDate') }}</span><strong class="android-repay-plan-item__date">{{ formatDisplayDate(item.dueDate) }}</strong></div>
              <div class="android-repay-plan-item__row"><span>{{ $t('orderPage.repayPlanAmount') }}</span><strong class="android-repay-plan-item__amount">{{ formatRupiah(item.total) }}</strong></div>
            </div>
          </div>
          <div class="android-repay-plan-sheet__notice android-repay-plan-sheet__notice--secure">
            <span class="android-repay-plan-sheet__notice-icon"><android-order-icon name="protect" :size="30" /></span>
            <div><strong>{{ $t('orderPage.repayPlanAutoChargeTitle') }}</strong><span>{{ $t('orderPage.repayPlanAutoChargeDesc') }}</span></div>
          </div>
        </div>
      </div>
    </Popup>
    <Popup v-model="showCancelDialog" position="bottom" class="cancel-popup-wrap">
      <div class="cancel-popup">
        <div class="cancel-popup__close" @click="showCancelDialog = false">
          <img src="@/assets/img/close.png" alt="">
        </div>
        <div class="cancel-popup__title">{{ $t('orderList.cancelTitle') }}</div>
        <div class="cancel-popup__content">
          <div class="cancel-popup__desc-card">{{ cancelDialogDesc }}</div>
          <div class="cancel-popup__summary-card">
            <div class="cancel-popup__product">
              <div class="cancel-popup__product-img" v-lazy:background-image="showData?.imageUrl || orderDetail?.goodsPic"></div>
              <div class="cancel-popup__product-info">
                <div class="cancel-popup__product-name">{{ orderDetail?.goodsName || $t('product') }}</div>
                <div v-if="cancelDialogSkuText" class="cancel-popup__product-sku">{{ cancelDialogSkuText }}</div>
              </div>
            </div>
            <div class="cancel-popup__divider">
              <span></span>
              <van-icon name="arrow-down" />
              <span></span>
            </div>
            <div class="cancel-popup__amount-block">
              <div class="cancel-popup__amount-head">
                <span>{{ cancelDialogAmountTitle }}</span>
                <span v-if="isCod" class="cancel-popup__unpaid-pill">{{ $t('rentalplan.unpaid') }}</span>
              </div>
              <div class="cancel-popup__amount-value">{{ priceUint }}{{ effectivePaymentAmount | numberFormat }}</div>
            </div>
          </div>
          <div v-if="showCancelRefundFields" class="cancel-popup__refund">
            <div class="cancel-popup__section-title">{{ $t('orderList.refundAccount') }}</div>
            <div class="cancel-popup__refund-notice">
              <div class="cancel-popup__refund-icon"><van-icon name="info-o" /></div>
              <div class="cancel-popup__refund-text">{{ $t('orderList.refundAccountNotice') }}</div>
            </div>
            <div class="cancel-popup__label">{{ $t('orderList.refundType') }}</div>
            <van-radio-group v-model="refundType" direction="horizontal" class="cancel-popup__radio-group">
              <van-radio name="BANK">{{ $t('orderList.bankCard') }}</van-radio>
              <van-radio name="VA">{{ $t('orderList.virtualAccount') }}</van-radio>
              <van-radio name="E_WALLET">{{ $t('orderList.eWallet') }}</van-radio>
            </van-radio-group>
            <div v-if="refundType" class="cancel-popup__field">
              <div class="cancel-popup__label">{{ $t('orderList.bankName') }}</div>
              <div class="cancel-popup__selector" @click="openBankPicker">{{ selectedBankName || bankSelectorPlaceholder }}</div>
            </div>
            <div v-if="refundType" class="cancel-popup__field">
              <div class="cancel-popup__label">{{ $t('orderList.accountNo') }}</div>
              <van-field v-model="refundAccountNo" :placeholder="$t('orderList.accountNoPlaceholder')" maxlength="30" />
            </div>
            <div v-if="refundType" class="cancel-popup__field">
              <div class="cancel-popup__label">{{ $t('orderList.accountName') }}</div>
              <van-field v-model="refundAccountName" :placeholder="$t('orderList.accountNamePlaceholder')" maxlength="64" />
            </div>
          </div>
          <div class="cancel-popup__reason-card">
            <div class="cancel-popup__section-title">{{ $t('orderList.cancelReason') }}</div>
            <div class="cancel-popup__reason">
              <van-field
                v-model="cancelReason"
                :rows="showCancelRefundFields ? 2 : 5"
                autosize
                type="textarea"
                maxlength="200"
                :placeholder="$t('orderList.cancelReasonPlaceholder')"
                show-word-limit
              />
            </div>
          </div>
          <div v-if="showCancelCodNotice" class="cancel-popup__cod-notice">
            <div class="cancel-popup__cod-icon"><van-icon name="info-o" /></div>
            <div class="cancel-popup__cod-copy">
              <div class="cancel-popup__cod-title">{{ $t('orderList.codCancelNoticeTitle') }}</div>
              <div class="cancel-popup__cod-desc">{{ $t('orderList.codCancelNoticeDesc') }}</div>
            </div>
          </div>
        </div>
        <div class="cancel-popup__footer">
          <button type="button" class="cancel-popup__footer-btn cancel-popup__footer-btn--secondary" @click="showCancelDialog = false">{{ $t('orderList.keepOrder') }}</button>
          <button type="button" class="cancel-popup__footer-btn cancel-popup__footer-btn--primary" @click="onCancelConfirm">{{ $t('confirm') }}</button>
        </div>
      </div>
    </Popup>
    <Picker ref="bankPicker" :columns="currentBankList" :valueKey="bankValueKey" :lock-scroll="false" @confirm="onBankPickerConfirm"></Picker>
    <van-dialog
      :value="showWithdrawDialog"
      class="android-withdraw-dialog"
      :show-confirm-button="false"
      :close-on-click-overlay="!actionSubmitting"
      @input="showWithdrawDialog = $event"
    >
      <div class="android-withdraw-dialog__content">
        <div class="android-withdraw-dialog__scene">
          <span class="android-withdraw-dialog__halo"></span>
          <span class="android-withdraw-dialog__icon"><van-icon name="warning" /></span>
        </div>
        <div class="android-withdraw-dialog__title">{{ $t('orderList.cancelStatus.withdrawTitle') }}</div>
        <div class="android-withdraw-dialog__message">{{ $t('orderList.cancelStatus.withdrawContent') }}</div>
        <div class="android-withdraw-dialog__actions">
          <button type="button" class="android-withdraw-dialog__button android-withdraw-dialog__button--cancel" :disabled="actionSubmitting" @click="showWithdrawDialog = false">{{ $t('cancel') }}</button>
          <button type="button" class="android-withdraw-dialog__button android-withdraw-dialog__button--confirm" :disabled="actionSubmitting" @click="doWithdrawCancel">{{ $t('confirm') }}</button>
        </div>
      </div>
    </van-dialog>
  </div>
</template>
<script>
import {Icon, Dialog, Field, Radio, RadioGroup, Popup, PullRefresh} from 'vant'
import AndroidOrderIcon from '@/components/AndroidOrderIcon.vue'
import { formatAddressForOrder } from '@/utils/address';
import { copyText } from '@/utils/clipboard';
import PaymentPopup from '@/components/PaymentPopup/index.vue'
import WithholdingAuthPopup from '@/components/WithholdingAuthPopup/index.vue'
import { hasWithholdingAuthShown, markWithholdingAuthShown } from '@/common/withholdingAuth';
import {
  queryOrderDetail,
  queryRepayPlan,
  queryExpressByOrderId,
  cancelOrder,
  withdrawCancelRefund,
  UserRefundApplyAuditStatus
} from '@/api/order';
import {ORDER_STATUS, LANGUAGE, BREACH_ORDER_STATUS, RELATIONSHIP_TYPE} from '@/common/constants';
import {getAppSystemConfig, queryBankList, getStoreDetail} from '@/api/common';
import Picker from '@/components/picker.vue';
import {queryAdditionalInfo} from '@/api/additionalInfo';
import {queryBreachTrial} from '@/api/breach';
import {applyDirectDebit} from '@/api/payment';
import {canSkipOrderAdditional, resolveRequiredSupplementTypes} from '@/common/supplement';
import {formatRentalDate, formatRentalDateTime} from '@/filters';
import routerBack from '@/mixins/routerBack';
import ExpressProgress from '@/components/ExpressProgress.vue';
import {buildCustomerSupportWhatsAppUrl} from '@/common/customerSupport';
import {formatRupiahAmount} from '@/utils/money';
import {getPaymentExpireTime, getPaymentRemainingSeconds} from '@/utils/date';
import { DEFAULT_REG_CHANNEL } from '@/config';

const RISK_AUDIT_STATUS = {
  PENDING: 'PENDING',
  REVIEWING: 'REVIEWING',
  PASSED: 'PASSED',
  FAILED: 'FAILED'
};

const COD_SHIPPING_FEE_PAY_STATUS = {
  PENDING_PAYMENT: 'PENDING_PAYMENT',
  SUCCESS: 'SUCCESS',
  CLOSED: 'CLOSED'
};
const LATEST_ORDER_ID_KEY = 'checkoutLatestOrderId';
const THIRD_PARTY_RETURN_QUERY = 'fromThirdParty';

export default {
  name: 'OrderConfirm',
  mixins: [routerBack],
  components: {
    PaymentPopup,
    WithholdingAuthPopup,
    ExpressProgress,
    Picker,
    Popup,
    Icon,
    'van-dialog': Dialog.Component,
    'van-field': Field,
    'van-icon': Icon,
    'van-pull-refresh': PullRefresh,
    'van-radio': Radio,
    'van-radio-group': RadioGroup,
    AndroidOrderIcon
  },
  computed: {
    isDelivery() {
      return this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.DELIVERY;
    },
    isCod() {
      return this.normalizeOrderStatus(this.orderDetail?.payMethod) === 'COD';
    },
    isStore() {
      return this.normalizeOrderStatus(this.orderDetail?.payMethod) === 'STORE';
    },
    pickupStoreId() {
      return this.isStore ? String(this.orderDetail?.storeId || '').trim() : '';
    },
    pickupStoreRequestKey() {
      return this.pickupStoreId ? `${this.orderDetail.orderId}:${this.pickupStoreId}` : '';
    },
    riskAuditStatus() {
      return this.normalizeOrderStatus(this.orderDetail?.riskAuditStatus);
    },
    isRiskAuditAwaitingApproval() {
      return this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.DELIVERY &&
        [RISK_AUDIT_STATUS.PENDING, RISK_AUDIT_STATUS.REVIEWING].includes(this.riskAuditStatus);
    },
    isCodApprovedAwaitingShipment() {
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      return this.isCod &&
        this.riskAuditStatus === RISK_AUDIT_STATUS.PASSED &&
        [ORDER_STATUS.DELIVERY, ORDER_STATUS.LOGISTICS].includes(status) &&
        !String(this.orderDetail?.expressId || '').trim();
    },
    isStoreApprovedAwaitingPayment() {
      return this.isStore &&
        this.riskAuditStatus === RISK_AUDIT_STATUS.PASSED &&
        !this.isClosedOrCancelled &&
        this.normalizeOrderStatus(this.orderDetail?.payStatus) !== 'SUCCESS';
    },
    isStoreApprovedPaidAwaitingShipment() {
      return this.isStore &&
        this.riskAuditStatus === RISK_AUDIT_STATUS.PASSED &&
        this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.LOGISTICS &&
        this.normalizeOrderStatus(this.orderDetail?.payStatus) === 'SUCCESS';
    },
    isCodShippingFeePaid() {
      return this.normalizeOrderStatus(this.orderDetail?.codShippingFeePayStatus) === COD_SHIPPING_FEE_PAY_STATUS.SUCCESS;
    },
    isCodShippingFeePendingPayment() {
      return this.normalizeOrderStatus(this.orderDetail?.codShippingFeePayStatus) === COD_SHIPPING_FEE_PAY_STATUS.PENDING_PAYMENT;
    },
    isCodShippingFeeClosed() {
      return this.normalizeOrderStatus(this.orderDetail?.codShippingFeePayStatus) === COD_SHIPPING_FEE_PAY_STATUS.CLOSED;
    },
    isBackTrackRefund() {
      return Boolean(this.orderDetail?.canBackTrack);
    },
    showCancelRefundFields() {
      return this.normalizeOrderStatus(this.orderDetail?.payStatus) === 'SUCCESS' && !this.isBackTrackRefund;
    },
    showCancelCodNotice() {
      return this.isCod;
    },
    cancelDialogDesc() {
      if (this.isCod) {
        return this.$t('orderList.cancelCodDesc');
      }
      if (this.normalizeOrderStatus(this.orderDetail?.payStatus) !== 'SUCCESS') {
        return this.$t('orderList.cancelContent');
      }
      if (this.isBackTrackRefund) {
        return this.$t('orderList.cancelBackTrackDesc');
      }
      return this.$t('orderList.cancelRefundDesc');
    },
    cancelDialogAmountTitle() {
      return this.$t(this.isCod || this.normalizeOrderStatus(this.orderDetail?.payStatus) !== 'SUCCESS' ? 'orderList.orderAmount' : 'orderList.cancelRefundAmount');
    },
    cancelDialogSkuText() {
      return this.showData?.skuStr || this.orderDetail?.skuInfo || '';
    },
    canOpenRepayPlan() {
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      return [
        ORDER_STATUS.PAYMENT,
        ORDER_STATUS.DELIVERY,
        ORDER_STATUS.LOGISTICS,
        ORDER_STATUS.RECEIVE,
        ORDER_STATUS.RETURN,
        ORDER_STATUS.BREACH,
        ORDER_STATUS.SUCCESS,
        ORDER_STATUS.CLOSED,
        ORDER_STATUS.CANCELLED
      ].includes(status);
    },
    isClosedOrCancelled() {
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      const cancelApplyStatus = this.normalizeOrderStatus(this.orderDetail?.cancelApplyStatus);
      const userCancelStatus = this.normalizeOrderStatus(this.orderDetail?.userCancelStatus);
      return [ORDER_STATUS.CLOSED, ORDER_STATUS.CANCELLED].includes(status) ||
        cancelApplyStatus === 'APPROVED' ||
        ['APPROVED', 'CANCELLED'].includes(userCancelStatus);
    },
    hasPaidInitialAmount() {
      const payStatus = this.normalizeOrderStatus(this.orderDetail?.payStatus);
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      return payStatus === 'SUCCESS' || (
        status !== ORDER_STATUS.PAYMENT &&
        !this.isClosedOrCancelled &&
        !this.shouldShowCodAmountDueOnDelivery
      );
    },
    isPaidOrder() {
      const payStatus = this.normalizeOrderStatus(this.orderDetail?.payStatus);
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      return payStatus === 'SUCCESS' &&
        ![ORDER_STATUS.CLOSED, ORDER_STATUS.CANCELLED, ORDER_STATUS.PAYMENT].includes(status);
    },
    shouldShowCodAmountDueOnDelivery() {
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      return this.isCod &&
        this.normalizeOrderStatus(this.orderDetail?.payStatus) !== 'SUCCESS' &&
        [ORDER_STATUS.PAYMENT, ORDER_STATUS.DELIVERY, ORDER_STATUS.LOGISTICS, ORDER_STATUS.RECEIVE].includes(status);
    },
    shouldShowCodPaymentReminder() {
      return this.shouldShowCodAmountDueOnDelivery;
    },
    showStorePaymentReminder() {
      return this.isStoreApprovedAwaitingPayment || this.isStoreApprovedPaidAwaitingShipment;
    },
    showCodShippingFeePaymentNotice() {
      return this.isCod &&
        this.codShippingFeeAmount > 0 &&
        this.isCodShippingFeePendingPayment &&
        !this.isClosedOrCancelled &&
        !this.isCodShippingFeeClosed;
    },
    showCodShippingFeeRefundNotice() {
      return this.isCod &&
        this.codShippingFeeAmount > 0 &&
        this.isCodShippingFeePaid;
    },
    showCodShippingFeeNotice() {
      return this.showCodShippingFeePaymentNotice || this.showCodShippingFeeRefundNotice;
    },
    codShippingFeeNoticeDesc() {
      return this.showCodShippingFeePaymentNotice
        ? this.$t('orderPage.codSuccessShippingFeeNoticeDesc')
        : this.$t('orderPage.codShippingFeeNoticeRefund');
    },
    effectivePaymentAmount() {
      const firstPayAmount = Number(this.orderDetail?.firstPayAmount || 0);
      return firstPayAmount > 0 ? firstPayAmount : Number(this.orderDetail?.totalAmount || 0);
    },
    codShippingFeeAmount() {
      return Number(this.orderDetail?.codShippingFee || this.showData?.codShippingFee || 0);
    },
    dueAfterApprovalAmount() {
      return this.codShippingFeeAmount;
    },
    codApprovalAmountLabel() {
      return this.isCodShippingFeePaid
        ? this.$t('orderPage.codShippingFeePaid')
        : this.$t('orderPage.amountDueOnApproval');
    },
    dueOnDeliveryAmount() {
      return this.addMoney(this.showData?.depositAmount, this.initialRentalPayment);
    },
    normalizedRentalCycleCode() {
      const cycle = this.normalizeOrderStatus(this.showData?.planUnit || this.orderDetail?.planUnit);
      if (['WEEK', 'WEEKLY', 'W'].includes(cycle)) return 'WEEKLY';
      if (['BI_WEEK', 'BIWEEKLY', 'BI-WEEKLY', 'BI_WEEKLY', 'B'].includes(cycle)) return 'BI_WEEKLY';
      if (['MONTH', 'MONTHLY', 'M'].includes(cycle)) return 'MONTHLY';
      return 'MONTHLY';
    },
    periodicRentLabel() {
      return this.$t(`orderPage.periodicRent.${this.normalizedRentalCycleCode}`);
    },
    periodicRentUnit() {
      return this.$t(`orderPage.rentCycleUnit.${this.normalizedRentalCycleCode}`);
    },
    rentalTermText() {
      const term = Number(this.showData?.rentalTerm || 0);
      let unit = this.periodicRentUnit;
      if (this.normalizedRentalCycleCode === 'MONTHLY' && term !== 1) unit = 'months';
      if (this.normalizedRentalCycleCode === 'WEEKLY' && term !== 1) unit = 'weeks';
      return `${term}${unit}`;
    },
    showCodShippingFeePayAction() {
      return this.isCod &&
        this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.DELIVERY &&
        this.riskAuditStatus === RISK_AUDIT_STATUS.PASSED &&
        this.codShippingFeeAmount > 0 &&
        this.isCodShippingFeePendingPayment;
    },
    showStorePayAtStoreAction() {
      return this.isStoreApprovedAwaitingPayment;
    },
    showStorePayAtStorePaidStatus() {
      return this.isStoreApprovedPaidAwaitingShipment;
    },
    showCodShippingFeePaidStatus() {
      return this.isCodApprovedAwaitingShipment &&
        this.codShippingFeeAmount > 0 &&
        this.isCodShippingFeePaid;
    },
    codShippingFeePaidStatusLabel() {
      return `${this.$t('orderPage.codShippingFee')} ( ${this.priceUint} ${formatRupiahAmount(this.codShippingFeeAmount)} )`;
    },
    codShippingFeePayActionText() {
      return `${this.$t('orderPage.payCodShippingFee')} ( ${this.priceUint} ${formatRupiahAmount(this.codShippingFeeAmount)} ) >>`;
    },
    storePayAtStoreActionText() {
      return `${this.$t('orderPage.payAtStore')} (${this.priceUint}${formatRupiahAmount(this.effectivePaymentAmount)}) >>`;
    },
    storePayAtStorePaidStatusText() {
      return `${this.$t('orderPage.paid')} (${this.priceUint}${formatRupiahAmount(this.effectivePaymentAmount)}) `;
    },
    pricePlanAmountLabel() {
      if (this.shouldShowCodAmountDueOnDelivery) return this.$t('orderPage.codAmountTitle');
      if (this.isStore && this.normalizeOrderStatus(this.orderDetail?.payStatus) !== 'SUCCESS') {
        return this.$t('orderPage.amountDueBeforePickup');
      }
      if (this.hasPaidInitialAmount) return this.$t('orderPage.amountPaid');
      return this.$t('orderPage.total');
    },
    showCancelledOrderNotices() {
      return this.isClosedOrCancelled;
    },
    showRentalPeriodOngoingNotice() {
      return this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.RETURN;
    },
    showPaymentReceivedNotice() {
      return !this.isClosedOrCancelled && !this.showRentalPeriodOngoingNotice && this.hasPaidInitialAmount;
    },
    showRemainingAmountNotice() {
      return !this.isClosedOrCancelled && !this.showRentalPeriodOngoingNotice && !this.showPaymentReceivedNotice && !this.isCod;
    },
    showPricePlanNotice() {
      return this.showCancelledOrderNotices || this.showRentalPeriodOngoingNotice || this.showPaymentReceivedNotice || this.showRemainingAmountNotice;
    },
    canApplyReturnFromRepayPlan() {
      return [ORDER_STATUS.RETURN, ORDER_STATUS.BREACH].includes(this.normalizeOrderStatus(this.orderDetail?.status));
    },
    repayPlanReturnActionText() {
      return this.isReturnedCompletedStatus ? this.$t('planDetail.returnDetails') : this.$t('planDetail.applyForReturn');
    },
    hasOverdueRepayPlan() {
      return this.repayPlanItems.some(item => item.statusClass === 'overdue');
    },
    repayPlanItems() {
      const planList = Array.isArray(this.repayPlan?.planList) ? this.repayPlan.planList : [];
      const deposit = Number(this.repayPlan?.depositAmount || 0);
      return planList.map((item, index) => ({ item, index })).filter(({ item }) => {
        return !this.isReturnedCompletedStatus || !['unpaid', 'upcoming'].includes(this.repayPlanStatusClass(item.status));
      }).map(({ item, index }) => {
        const baseTotal = Number(item.total || item.rentalAmount || 0);
        return {
          ...item,
          total: index === 0 ? Math.round((baseTotal + deposit) * 100) / 100 : baseTotal,
          dueDate: this.resolveRepayPlanDueDate(item),
          statusClass: this.repayPlanStatusClass(item.status),
          statusText: this.repayPlanStatusText(item.status),
          statusIconName: this.repayPlanStatusIcon(item.status)
        };
      });
    },
    showData() {
      if (!this.orderDetail) {
        return null;
      }
      let {
        orderId,
        createTime,
        payTime,
        goodsName,
        goodsPic,
        skuInfo,
        firstPayAmount,
        riskAmount,
        depositAmount,
        planPrice,
        planIndex,
        planUnit,
        codShippingFee,
        riskAuditStatus,
        codShippingFeePayStatus,
        rentDate,
        backDate,
        recipientName,
        recipientPhone,
        detailedAddress,
        province,
        provinceEn,
        provinceId,
        provincePh,
        region,
        regionEn,
        regionId,
        regionPh,
        city,
        cityEn,
        cityId,
        cityPh,
        emergencyContactList = []
      } = this.orderDetail || {};
      emergencyContactList = Array.isArray(emergencyContactList) ? emergencyContactList : [];
      let address = {
        recipientName,
        recipientPhone,
        detailedAddress,
        province: this.localizedAddressValue({ en: provinceEn, id: provinceId, ph: provincePh, fallback: province }),
        region: this.localizedAddressValue({ en: regionEn, id: regionId, ph: regionPh, fallback: region }),
        city: this.localizedAddressValue({ en: cityEn, id: cityId, ph: cityPh, fallback: city }),
      }
      return {
        orderId,
        createTime,
        payTime,
        goodsName,
        imageUrl: goodsPic,
        skuStr: this.formatSkuSummary(skuInfo, planIndex, planUnit),
        firstPayAmount,
        riskAmount,
        firstRentalAmount: [planPrice, riskAmount].reduce((total, amount) => total + Math.round((Number(amount) || 0) * 100), 0) / 100,
        totalRentalPayments: [planPrice * (planIndex || 0), riskAmount]
          .reduce((total, amount) => total + Math.round((Number(amount) || 0) * 100), 0) / 100,
        depositAmount,
        planPrice,
        codShippingFee,
        riskAuditStatus,
        codShippingFeePayStatus,
        planIndex,
        remainingRent: Math.max(Number(planIndex) || 0, 0) - 1,
        rentalTerm: Math.max(Number(planIndex) || 0, 0),
        planUnit,
        rentalStartDate: rentDate,
        rentalEndDate: backDate,
        rentalCycle: this.$t(`cycle.${planUnit}`),
        address,
        emergencyContactList
      }
    },
    address() {
      return this.showData?.address || null;
    },
    addressDetail() {
      if (!this.address) {
        return '';
      } else {
        return formatAddressForOrder(this.address, '--');
      }
    },
    isDetail() {
      return !!this.orderId;
    },
    title() {
      return this.isDetail ? this.$t('orderPage.orderTitle') : this.$t('orderPage.confirmTitle');
    },
    priceUint() {
      return this.$t('priceUnit');
    },
    breachOrderStatus() {
      return this.normalizeOrderStatus(this.orderDetail?.breachOrderStatus);
    },
    isReturnedCompletedStatus() {
      return this.breachOrderStatus === BREACH_ORDER_STATUS.COMPLETED ||
        this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.BREACH;
    },
    isReturningStatus() {
      return this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.RETURN &&
        this.breachOrderStatus === BREACH_ORDER_STATUS.PROCESSING;
    },
    mainOrderStatusText() {
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      if (status === ORDER_STATUS.PAYMENT) return this.$t('orderList.status.waitingPay');
      if ([ORDER_STATUS.CLOSED, ORDER_STATUS.CANCELLED].includes(status)) return this.$t('orderList.status.closed');
      if (status === ORDER_STATUS.SUCCESS) return this.$t('orderList.status.completed');
      if (status === ORDER_STATUS.BREACH) return this.$t('orderList.status.breach');
      if (status === ORDER_STATUS.RETURN && this.breachOrderStatus === BREACH_ORDER_STATUS.PROCESSING) {
        return this.$t('orderPage.returningTitle');
      }
      if ([ORDER_STATUS.DELIVERY, ORDER_STATUS.LOGISTICS, ORDER_STATUS.RECEIVE, ORDER_STATUS.RETURN].includes(status)) {
        return this.$t('orderList.status.underLease');
      }
      return '';
    },
    subOrderStatusText() {
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      if (status === ORDER_STATUS.DELIVERY) {
        return this.isRiskAuditAwaitingApproval
          ? this.$t('orderPage.awaitingApproval')
          : this.$t('orderPage.statusAwaitingShipment');
      }
      if (status === ORDER_STATUS.LOGISTICS) return this.$t('orderPage.statusAwaitingShipment');
      if (status === ORDER_STATUS.RECEIVE) return this.$t('orderPage.statusAwaitingDelivery');
      if (status === ORDER_STATUS.RETURN) {
        return this.breachOrderStatus === BREACH_ORDER_STATUS.PROCESSING
          ? this.$t('orderList.status.breaching')
          : this.$t('orderList.logistics.signed');
      }
      return '';
    },
    statusClass() {
      if (this.isReturnedCompletedStatus) {
        return 'status-green';
      }
      if (this.isReturningStatus) {
        return 'status-purple';
      }
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      if (status === ORDER_STATUS.PAYMENT) {
        return 'status-orange';
      } else if ([ORDER_STATUS.DELIVERY,ORDER_STATUS.LOGISTICS, ORDER_STATUS.RECEIVE, ORDER_STATUS.RETURN, ORDER_STATUS.BREACH].includes(status)) {
        return 'status-blue';
      } else if (status === ORDER_STATUS.SUCCESS) {
        return 'status-green';
      } else if ([ORDER_STATUS.CLOSED, ORDER_STATUS.CANCELLED].includes(status)) {
        return 'status-gray';
      }
      return '';
    },
    statusText() {
      if (!this.isDetail) {
        return '';
      }
      return this.mainOrderStatusText;
    },
    showEstimatedRentalDateLabel() {
      return ![ORDER_STATUS.RETURN, ORDER_STATUS.SUCCESS, ORDER_STATUS.BREACH].includes(this.normalizeOrderStatus(this.orderDetail?.status));
    },
    statusIconName() {
      if (this.isReturnedCompletedStatus) return 'checked';
      // Android OrderDetailStatusCard uses Icons.Default.LocalShipping for Returning.
      if (this.isReturningStatus) return 'logistics';
      if (this.isRiskAuditAwaitingApproval) return 'checked';
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      if (status === ORDER_STATUS.PAYMENT) return 'clock-o';
      if (status === ORDER_STATUS.SUCCESS) return 'checked';
      if ([ORDER_STATUS.CLOSED, ORDER_STATUS.CANCELLED].includes(status)) return 'close';
      return 'logistics';
    },
    statusCardTitle() {
      if (this.isReturnedCompletedStatus) return this.$t('orderPage.returnCompletedTitle');
      return this.mainOrderStatusText || this.$t('orderPage.orderTitle');
    },
    statusCardBadge() {
      if (this.isReturningStatus) return this.$t('orderPage.returnInProgress');
      if (!this.subOrderStatusText || this.subOrderStatusText === this.mainOrderStatusText) return '';
      return this.subOrderStatusText;
    },
    statusCardDescription() {
      if (this.isClosedOrCancelled) return this.$t('orderPage.orderCancelledDesc');
      if (this.isRiskAuditAwaitingApproval) return this.$t('orderPage.awaitingApprovalDesc');
      if (this.isStoreApprovedPaidAwaitingShipment) return this.$t('orderPage.approvedAwaitingShipmentDesc');
      if (this.isStoreApprovedAwaitingPayment) return this.$t('orderPage.approvedAwaitingShipmentDesc');
      if (this.isReturnedCompletedStatus) return this.$t('orderPage.returnCompletedDescription');
      if (this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.SUCCESS) return this.$t('orderPage.orderSuccessCompletedDescription');
      if (this.isReturningStatus) return this.$t('orderPage.returningDescription');
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      if (status === ORDER_STATUS.DELIVERY || this.isCodApprovedAwaitingShipment) {
        return this.isCod && this.riskAuditStatus === RISK_AUDIT_STATUS.PASSED
          ? this.$t('orderPage.approvedAwaitingShipmentDesc')
          : this.$t('orderPage.awaitingShipmentDesc');
      }
      if ([ORDER_STATUS.LOGISTICS, ORDER_STATUS.RECEIVE].includes(status)) return this.$t('orderPage.awaitingDeliveryDesc');
      if (status === ORDER_STATUS.RETURN) return this.$t('orderPage.deliveredDescription');
      return '';
    },
    statusCardTime() {
      if (this.isClosedOrCancelled && this.orderDetail?.closeTime) return this.closeTimeText;
      if (this.showCodShippingFeePayAction || this.showCodShippingFeePaidStatus) return '';
      if (this.isStoreApprovedAwaitingPayment) return '';
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      if (status === ORDER_STATUS.DELIVERY || this.isCodApprovedAwaitingShipment) {
        const time = this.isCod ? this.orderDetail?.createTime : this.orderDetail?.payTime;
        return time ? formatRentalDateTime(time, '') : '';
      }
      if (status === ORDER_STATUS.RETURN && !this.isReturningStatus && !this.isReturnedCompletedStatus) {
        return this.orderDetail?.receiveTime ? formatRentalDateTime(this.orderDetail.receiveTime, '') : '';
      }
      return '';
    },
    statusCardTimeFormatKey() {
      if (this.isClosedOrCancelled) return 'orderPage.cancelledOnFormat';
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      if (status === ORDER_STATUS.DELIVERY || this.isCodApprovedAwaitingShipment) {
        return this.isCod ? 'orderPage.orderedOnFormat' : 'orderPage.paidOnFormat';
      }
      if (status === ORDER_STATUS.RETURN) return 'orderPage.deliveredOnFormat';
      return '';
    },
    statusCardTimeLabel() {
      if (!this.statusCardTime) return '';
      return this.statusCardTimeFormatKey
        ? this.$t(this.statusCardTimeFormatKey, { time: this.statusCardTime })
        : this.statusCardTime;
    },
    showPricePlanCloseReason() {
      return this.showCloseReasonInfoRow;
    },
    showLogisticsCard() {
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      if (![ORDER_STATUS.LOGISTICS, ORDER_STATUS.RECEIVE].includes(status)) {
        return false;
      }
      return !!String(this.orderDetail?.expressId || '').trim();
    },
    logisticsIcon() {
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      if (status === ORDER_STATUS.DELIVERY) return 'paid';
      if (status === ORDER_STATUS.LOGISTICS) return 'logistics';
      if (status === ORDER_STATUS.RECEIVE) return 'logistics';
      if (status === ORDER_STATUS.RETURN) return 'certificate';
      return 'logistics';
    },
    logisticsStatusText() {
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      if ([ORDER_STATUS.DELIVERY, ORDER_STATUS.LOGISTICS].includes(status)) return this.$t('orderPage.statusAwaitingShipment');
      if (status === ORDER_STATUS.RECEIVE) return this.$t('orderPage.viewLogisticsProgress');
      if (status === ORDER_STATUS.RETURN) return this.$t('orderList.logistics.signed');
      return '';
    },
    showPaymentCountdown() {
      return this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.PAYMENT && !this.isCod;
    },
    showPaymentAction() {
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      return this.isDetail &&
        !this.isCod &&
        status === ORDER_STATUS.PAYMENT &&
        this.hasOnlinePaymentInfo(this.orderDetail);
    },
    showAwaitingShipmentAction() {
      return this.isDetail &&
        (
          (this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.DELIVERY &&
            (this.showCancelBtn || this.showCodShippingFeePayAction)) ||
          this.showStorePayAtStoreAction
        );
    },
    showBottomCancelOrderAction() {
      return this.showCancelBtn && (
        this.showPaymentAction ||
        this.showCodShippingFeePayAction ||
        this.showStorePayAtStoreAction ||
        this.isStoreApprovedPaidAwaitingShipment
      );
    },
    paymentActionText() {
      return this.$t('orderPage.payNow');
    },
    isPaymentExpired() {
      const createTime = this.orderDetail?.createTimeTs || this.orderDetail?.createTime;
      if (!createTime || this.normalizeOrderStatus(this.orderDetail?.status) !== ORDER_STATUS.PAYMENT) {
        return false;
      }
      const expireTime = getPaymentExpireTime(createTime);
      return this.paymentCountdown <= 0 || (expireTime ? this.currentTime >= expireTime : false);
    },
    showCancelBtn() {
      const cancelStatus = this.normalizeOrderStatus(this.orderDetail?.cancelApplyStatus);
      if (![UserRefundApplyAuditStatus.NONE, UserRefundApplyAuditStatus.CANCELLED, UserRefundApplyAuditStatus.REJECTED, ''].includes(cancelStatus)) return false;
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      if (!this.isDetail) return false;
      if (this.isCod) {
        return status === ORDER_STATUS.DELIVERY;
      }
      if (this.isStoreApprovedAwaitingPayment) return true;
      if (this.isStoreApprovedPaidAwaitingShipment) return true;
      if (![ORDER_STATUS.PAYMENT, ORDER_STATUS.DELIVERY].includes(status)) return false;
      return true;
    },
    initialRentalPayment() {
      if (this.isCod) {
        return this.showData?.firstRentalAmount || (this.showData?.firstPayAmount || 0) - (this.showData?.depositAmount || 0);
      }
      return this.showData?.firstRentalAmount || this.showData?.planPrice || 0;
    },
    codReminderAmountText() {
      return formatRupiahAmount(this.showData?.firstPayAmount || 0);
    },
    codReminderIntroText() {
      return this.$t('orderPage.codPaymentReminderIntro');
    },
    codReminderMessageParts() {
      const amount = this.codReminderAmountText;
      const desc = this.$t('orderPage.codPaymentReminderDesc', { amount });
      const candidates = [`${this.priceUint}${amount}`, `${this.priceUint} ${amount}`, amount];
      const matched = candidates
        .map(value => ({ value, index: desc.indexOf(value) }))
        .find(item => item.index >= 0);

      if (!matched) {
        return { prefix: desc, amount: '', suffix: '' };
      }

      return {
        prefix: desc.slice(0, matched.index),
        amount,
        suffix: desc.slice(matched.index + matched.value.length)
      };
    },
    codReminderDescHtml() {
      const { prefix, amount, suffix } = this.codReminderMessageParts;
      const highlightedAmount = '<span style="color:#0B45FF;font-weight:400">' + amount + '</span>';
      return [
        this.codReminderIntroText,
        `${this.priceUint} ${prefix}${highlightedAmount}${suffix}`
      ].join('<br/>');
    },
    paymentDateText() {
      if (!this.showData?.payTime) {
        return '-';
      }
      return formatRentalDateTime(this.showData.payTime);
    },
    showCloseTimeRow() {
      return [ORDER_STATUS.CLOSED, ORDER_STATUS.CANCELLED].includes(this.normalizeOrderStatus(this.orderDetail?.status)) && !!this.orderDetail?.closeTime;
    },
    showCloseReasonRow() {
      return [ORDER_STATUS.CLOSED, ORDER_STATUS.CANCELLED].includes(this.normalizeOrderStatus(this.orderDetail?.status)) && !!this.closeReasonText;
    },
    showCloseReasonInStatusRow() {
      return this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.CLOSED && !!this.closeReasonText;
    },
    showCloseReasonStatusRow() {
      return this.showCloseReasonRow && (this.isCod || this.showCloseReasonInStatusRow);
    },
    showCloseReasonInfoRow() {
      return this.showCloseReasonRow && !this.isCod && !this.showCloseReasonInStatusRow;
    },
    closeReasonText() {
      return this.closeReasonMessage(this.orderDetail);
    },
    closeTimeText() {
      if (!this.orderDetail?.closeTime) {
        return '-';
      }
      return formatRentalDateTime(this.orderDetail.closeTime);
    },
    showAdditionalInfoBanner() {
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      if (status === ORDER_STATUS.PAYMENT ||
          status === ORDER_STATUS.RETURN ||
          status === ORDER_STATUS.BREACH ||
          status === ORDER_STATUS.CLOSED ||
          status === ORDER_STATUS.CANCELLED) {
        return false;
      }
      return this.additionalInfoEnabled &&
        this.additionalInfoLoaded &&
        !canSkipOrderAdditional(this.additionalInfoData || {}, this.additionalInfoRequiredTypes);
    },
    showCancelStatusCard() {
      // 待发货、已支付的门店待物流订单及已结束订单展示取消申请状态。
      const status = this.normalizeOrderStatus(this.orderDetail?.status);
      if (status !== ORDER_STATUS.DELIVERY &&
          status !== ORDER_STATUS.CANCELLED &&
          status !== ORDER_STATUS.CLOSED &&
          !this.isStoreApprovedPaidAwaitingShipment) {
        return false;
      }
      const s = this.normalizeOrderStatus(this.orderDetail?.cancelApplyStatus);
      return ['PENDING', 'APPLIED', 'APPROVED', 'REJECTED'].includes(s);
    },
    cancelStatusCardClass() {
      const s = this.normalizeOrderStatus(this.orderDetail?.cancelApplyStatus);
      if (s === 'REJECTED') return 'android-order-cancel-card--rejected';
      return 'android-order-cancel-card--pending';
    },
    cancelStatusIcon() {
      return this.normalizeOrderStatus(this.orderDetail?.cancelApplyStatus) === 'REJECTED' ? 'close' : 'clock-o';
    },
    cancelStatusTitle() {
      const s = this.normalizeOrderStatus(this.orderDetail?.cancelApplyStatus);
      if (s === 'APPROVED') return this.$t('orderList.cancelStatus.titleApproved');
      if (s === 'REJECTED') return this.$t('orderList.cancelStatus.titleRejected');
      return this.$t('orderList.cancelStatus.titlePending');
    },
    cancelStatusSubtitle() {
      const s = this.normalizeOrderStatus(this.orderDetail?.cancelApplyStatus);
      if (s === 'PENDING' || s === 'APPLIED') return this.$t('orderList.cancelStatus.subtitleUnderReview');
      return '';
    },
    cancelStatusInlineStatus() {
      return this.cancelStatusSubtitle ? `(${this.cancelStatusSubtitle})` : '';
    },
    cancelStatusDesc() {
      const s = this.normalizeOrderStatus(this.orderDetail?.cancelApplyStatus);
      if (s === 'PENDING' || s === 'APPLIED') return this.$t('orderList.cancelStatus.descReviewing');
      if (s === 'APPROVED') return this.$t('orderList.cancelStatus.descRefunded');
      return '';
    },
    cancelApplyTimeText() {
      return this.orderDetail?.cancelApplyTime ? formatRentalDateTime(this.orderDetail.cancelApplyTime) : '';
    },
    cancelAuditTimeText() {
      return this.orderDetail?.cancelRejectOrPassTime ? formatRentalDateTime(this.orderDetail.cancelRejectOrPassTime) : '';
    },
    showCancelStatusTime() {
      const s = this.normalizeOrderStatus(this.orderDetail?.cancelApplyStatus);
      const closeType = String(this.orderDetail?.closeType || '').trim().toLowerCase();
      return !(s === 'APPROVED' && closeType === 'rejectdelivery' && !this.isCod);
    },
    cancelCloseReason() {
      if (this.normalizeOrderStatus(this.orderDetail?.cancelApplyStatus) === 'REJECTED') {
        return this.orderDetail?.cancelAuditRemark || '';
      }
      return this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.CANCELLED ? this.closeReasonText : '';
    },
    cancelStatusRows() {
      const s = this.normalizeOrderStatus(this.orderDetail?.cancelApplyStatus);
      const rows = [];
      if (s === 'PENDING' || s === 'APPLIED') {
        if (this.cancelApplyTimeText) {
          rows.push({
            key: 'applied',
            text: this.$t('orderList.cancelStatus.appliedOnFormat', { time: this.cancelApplyTimeText })
          });
        }
        if (this.cancelStatusDesc) {
          rows.push({ key: 'reviewing', text: this.cancelStatusDesc });
        }
        return rows;
      }
      if (s === 'APPROVED') {
        if (this.cancelStatusDesc) {
          rows.push({ key: 'refunded', text: this.cancelStatusDesc });
        }
        return rows;
      }
      if (s === 'REJECTED') {
        if (this.cancelAuditTimeText) {
          rows.push({
            key: 'rejected',
            text: this.$t('orderList.cancelStatus.rejectedOnFormat', { time: this.cancelAuditTimeText })
          });
        }
        rows.push({
          key: 'reason',
          text: this.$t('orderList.cancelStatus.rejectReasonFormat', { reason: this.cancelCloseReason || '-' })
        });
        rows.push({ key: 'service', text: this.$t('orderList.cancelStatus.contactService') });
      }
      return rows;
    },
    showWithdrawCancelBtn() {
      const s = this.normalizeOrderStatus(this.orderDetail?.cancelApplyStatus);
      return s === 'PENDING' || s === 'APPLIED';
    },
    currentBankList() {
      if (this.refundType === 'E_WALLET') {
        return this.bankListType2;
      }
      return this.bankListType1;
    },
    bankSelectorPlaceholder() {
      switch (this.refundType) {
        case 'BANK': return this.$t('orderList.selectBankPlaceholder')
        case 'VA': return this.$t('orderList.selectVAPlaceholder')
        case 'E_WALLET': return this.$t('orderList.selectEWalletPlaceholder')
        default: return ''
      }
    }
  },
  watch: {
    pickupStoreRequestKey() {
      this.loadPickupStore();
    },
    refundType() {
      this.selectedBankId = ''
      this.selectedBankName = ''
      this.refundAccountNo = ''
      this.refundAccountName = ''
    },
    showPayUrlPopup(show) {
      if (!show && !this.isCod && this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.PAYMENT && !this.isPaymentExpired) {
        this.startPolling();
      }
    },
    repayPlanPopupVisible(show) {
      if (show) {
        this.$nextTick(() => this.resetRepayPlanScroll());
      }
    }
  },
  data() {
    return {
      orderId: null,
      orderDetail: null,
      pickupStore: null,
      storeLoading: false,
      storeRequestVersion: 0,
      refreshing: false,
      detailLoading: false,
      detailLoadError: false,
      paymentPopupVisible: false,
      // 添加轮询相关属性
      pollingInterval: null,
      pollingDelay: 30000,
      maxPollingAttempts: 1000,
      currentPollingAttempts: 0,
      express: null,
      showCancelDialog: false,
      cancelReason: '',
      refundType: '',
      bankListType1: [],
      bankListType2: [],
      selectedBankId: '',
      selectedBankName: '',
      refundAccountNo: '',
      refundAccountName: '',
      bankListLoaded: false,
      bankValueKey: 'bankName',
      showWithdrawDialog: false,
      additionalInfoStatus: '',
      additionalInfoData: null,
      additionalInfoRequiredTypes: resolveRequiredSupplementTypes(),
      additionalInfoEnabled: true,
      additionalInfoLoaded: false,
      currentTime: Date.now(),
      paymentExpireTimer: null,
      paymentCountdown: 30 * 60,
      paymentCountdownText: '00:30:00',
      paymentCountdownEndTime: null,
      showPayUrlPopup: false,
      repayPlanPopupVisible: false,
      repayPlanLoading: false,
      repayPlanError: false,
      repayPlan: null,
      checkingPaymentStatus: false,
      actionSubmitting: false,
      showWithholdingAuthPopup: false,
      directDebitAuthorizing: false,
      memberInfoFetched: false,
      autoDebitEnabled: false,
      autoDebitConfigPromise: null,
      expandedSections: {
        store: true,
        address: true,
        contacts: false,
        product: true,
        dates: true,
        price: true
      },
    }
  },
  mounted() {
    this.initOrderDetailFromRoute(this.$route);
  },
  methods: {
    async loadPickupStore() {
      const requestKey = this.pickupStoreRequestKey;
      const version = ++this.storeRequestVersion;
      this.pickupStore = null;
      this.storeLoading = false;
      if (!requestKey) return;
      this.storeLoading = true;
      try {
        const res = await getStoreDetail(this.pickupStoreId);
        if (version !== this.storeRequestVersion || requestKey !== this.pickupStoreRequestKey) return;
        this.pickupStore = res?.code === 'Success' && res.data ? res.data : null;
      } catch (e) {
        if (version === this.storeRequestVersion) this.pickupStore = null;
      } finally {
        if (version === this.storeRequestVersion) this.storeLoading = false;
      }
    },
    formatStoreBusinessHour(value) {
      const rawValue = String(value || '').trim();
      const range = rawValue.match(/^(\d{1,2}):(\d{2})\s*[-–—]\s*(\d{1,2}):(\d{2})$/);
      if (!range) return rawValue;
      const formatTime = (hourText, minuteText) => {
        const hour = Number(hourText);
        const minute = Number(minuteText);
        if (hour > 23 || minute > 59) return '';
        return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      };
      const start = formatTime(range[1], range[2]);
      const end = formatTime(range[3], range[4]);
      return start && end ? `${start} – ${end}` : rawValue;
    },
    initOrderDetailFromRoute(route = this.$route, { reload = true } = {}) {
      const orderId = this.resolveOrderIdFromRoute(route);
      this.scrollToTop();
      if (!orderId) {
        this.$router.replace('/order/confirm');
        return false;
      }
      const previousOrderId = this.orderId;
      this.orderId = orderId;
      this.cleanThirdPartyReturnUrl(route, orderId);
      if (reload || previousOrderId !== orderId) {
        this.getOrderDetail();
        this.queryAdditionalInfoStatus();
      }
      return true;
    },
    resolveOrderIdFromRoute(route = this.$route) {
      const queryOrderId = String(route.query?.orderId || '').trim();
      if (queryOrderId) {
        return queryOrderId;
      }
      if (!this.isThirdPartyReturnRoute(route)) {
        return '';
      }
      if (typeof sessionStorage !== 'undefined') {
        const sessionOrderId = String(sessionStorage.getItem(LATEST_ORDER_ID_KEY) || '').trim();
        if (sessionOrderId) return sessionOrderId;
      }
      if (typeof localStorage !== 'undefined') {
        return String(localStorage.getItem(LATEST_ORDER_ID_KEY) || '').trim();
      }
      return '';
    },
    isThirdPartyReturnRoute(route = this.$route) {
      return String(route.query?.[THIRD_PARTY_RETURN_QUERY] || '') === '1';
    },
    cleanThirdPartyReturnUrl(route = this.$route, orderId = this.orderId) {
      const queryOrderId = String(route.query?.orderId || '').trim();
      if (!this.isThirdPartyReturnRoute(route) && queryOrderId === orderId) {
        return;
      }
      this.$router.replace({
        path: route.path,
        query: { orderId }
      });
    },
    closeReasonMessage(order = {}) {
      const closeType = String(order?.closeType || '').trim().toLowerCase();
      const closeReason = String(order?.closeReason || '').trim();
      if (closeType === 'paymentfailed') {
        return this.$t('orderPage.paymentTimeoutCancelReason');
      }
      if (closeType === 'usercancelled') {
        return String(order?.cancelApplyReason || '').trim() ||
          closeReason ||
          this.$t('orderPage.userCancelledCloseReason');
      }
      if (closeType === 'rejectdelivery' || closeType === 'rejectsign') {
        return closeReason;
      }
      return closeReason;
    },
    scrollToTop() {
      this.$nextTick(() => {
        window.scrollTo(0, 0);
      });
    },
    async onRefresh() {
      if (!this.orderId) {
        this.refreshing = false;
        return;
      }
      try {
        await Promise.all([
          this.getOrderDetail({ silent: true }),
          this.queryAdditionalInfoStatus()
        ]);
      } finally {
        this.refreshing = false;
      }
    },
    normalizeOrderStatus(value = '') {
      return String(value == null ? '' : value).trim().toUpperCase();
    },
    addMoney(...amounts) {
      return amounts.reduce((total, amount) => total + Math.round((Number(amount) || 0) * 100), 0) / 100;
    },
    hasOnlinePaymentInfo(order = this.orderDetail) {
      return !!(String(order?.payContent || '').trim() || String(order?.payUrl || '').trim());
    },
    formatSkuSummary(skuInfo, planIndex, planUnit) {
      const cycleKey = this.normalizeOrderStatus(planUnit);
      const cycle = cycleKey && this.$te && this.$te(`cycle.${cycleKey}`) ? this.$t(`cycle.${cycleKey}`) : cycleKey;
      return [skuInfo, Number(planIndex) > 0 ? Number(planIndex) : '', cycle]
        .map(value => String(value == null ? '' : value).trim())
        .filter(Boolean)
        .join(' · ') || '--';
    },
    repayPlanPeriodSuffix(period) {
      const locale = this.$i18n?.locale || localStorage.getItem('language') || LANGUAGE.en;
      if (locale === LANGUAGE.id) return '';
      const value = Math.max(Number(period) || 0, 0);
      if (value >= 11 && value <= 13) return 'th';
      return {1: 'st', 2: 'nd', 3: 'rd'}[value % 10] || 'th';
    },
    repayPlanPeriodText(period) {
      return this.$t('orderPage.repayPlanPeriodFormat', {
        period,
        suffix: this.repayPlanPeriodSuffix(period)
      });
    },
    formatRupiah(value) {
      return `${this.priceUint}${formatRupiahAmount(value) || '0'}`;
    },
    formatDisplayDate(value) {
      return formatRentalDate(value);
    },
    resolveRepayPlanDueDate(item = {}) {
      const candidates = [
        item.paidTime,
        item.paid_time,
        item.repayDay,
        item.repay_day,
        item.dueDate,
        item.due_date,
        item.billDate,
        item.bill_date,
        item.planDate,
        item.plan_date,
        item.repayDate,
        item.repay_date
      ];
      return candidates.find(value => this.parseDisplayDate(value)) || '';
    },
    parseDisplayDate(value) {
      if (value === null || value === undefined || String(value).trim() === '' || String(value).trim() === '0') return null;
      if (typeof value === 'number' || /^\d+$/.test(String(value))) {
        const numeric = Number(value);
        const timestamp = numeric < 1000000000000 ? numeric * 1000 : numeric;
        const date = new Date(timestamp);
        return Number.isNaN(date.getTime()) || date.getFullYear() < 2001 ? null : date;
      }
      const normalized = String(value).trim().replace(/-/g, '/');
      const date = new Date(normalized);
      return Number.isNaN(date.getTime()) || date.getFullYear() < 2001 ? null : date;
    },
    localizedAddressValue({ en, id, ph, fallback } = {}) {
      const locale = this.$i18n?.locale || localStorage.getItem('language') || LANGUAGE.en;
      if (locale === LANGUAGE.en) return en || fallback || id || ph || '';
      if (locale === LANGUAGE.ph) return ph || fallback || id || en || '';
      return id || fallback || en || ph || '';
    },
    formatIndonesiaPhone(phone = '') {
      const digits = String(phone || '').replace(/\D/g, '');
      if (!digits) return '';
      const normalized = digits.startsWith('62')
        ? digits
        : `62${digits.replace(/^0/, '')}`;
      const local = normalized.slice(2);
      const groups = [];
      if (local) {
        groups.push(local.slice(0, 3));
        for (let index = 3; index < local.length; index += 4) {
          groups.push(local.slice(index, index + 4));
        }
      }
      return groups.length ? `+62 ${groups.join(' ')}` : '+62';
    },
    relationshipText(contact = {}) {
      const relationValue = contact.relationship?.value || contact.relationship || contact.relation?.value || contact.relation || '';
      const relation = RELATIONSHIP_TYPE.find(item => item.value === this.normalizeOrderStatus(relationValue));
      if (!relation) return '';
      const locale = this.$i18n?.locale || localStorage.getItem('language') || LANGUAGE.en;
      if (locale === LANGUAGE.id) return relation.textId;
      if (locale === LANGUAGE.ph) return relation.textPh;
      return relation.textEn;
    },
    async queryAdditionalInfoStatus() {
      this.additionalInfoLoaded = false;
      try {
        const configRes = await getAppSystemConfig({
          platform: 'h5',
          channel: this.$store.state.user.channel || DEFAULT_REG_CHANNEL
        });
        if (configRes?.code === 'Success') {
          const features = configRes.data?.features || configRes.data || {};
          this.additionalInfoEnabled = [true, 'true', 1, '1'].includes(features.additionInfo);
          this.additionalInfoRequiredTypes = resolveRequiredSupplementTypes(features);
        }
      } catch (e) {
        // 配置加载失败时继续使用 FAMILY_CARD 兜底查询，保持既有订单入口可用。
      }
      if (!this.additionalInfoEnabled) {
        this.additionalInfoLoaded = true;
        return;
      }
      try {
        const res = await queryAdditionalInfo();
        if (res?.code === 'Success') {
          this.additionalInfoData = res.data || {};
          this.additionalInfoStatus = this.additionalInfoData.overallStatus || '';
          this.additionalInfoLoaded = true;
        }
      } catch (e) {
        // 查询失败时不展示可能误导用户的补件入口。
      }
    },
    goAdditionalInfo() {
      this.$router.push({
        path: '/additional-info',
        query: {
          returnTo: 'orderDetail',
          orderId: this.orderId
        }
      })
    },
    async goRepayPlan() {
      if (!this.orderId || !this.canOpenRepayPlan || this.repayPlanLoading) return;
      this.repayPlanPopupVisible = true;
      await this.loadRepayPlan();
      this.$nextTick(() => this.resetRepayPlanScroll());
    },
    resetRepayPlanScroll() {
      const popup = this.$refs.repayPlanPopup;
      const root = popup && (popup.$el || popup);
      const body = root && root.querySelector('.android-repay-plan-sheet__body');
      if (body) body.scrollTop = 0;
    },
    async loadRepayPlan() {
      if (!this.orderId) return;
      this.repayPlanLoading = true;
      this.repayPlanError = false;
      try {
        if ([ORDER_STATUS.PAYMENT, ORDER_STATUS.DELIVERY, ORDER_STATUS.LOGISTICS, ORDER_STATUS.RECEIVE, ORDER_STATUS.CLOSED].includes(this.normalizeOrderStatus(this.orderDetail?.status))) {
          this.repayPlan = this.createRepayPlanPreview();
          return;
        }
        const res = await queryRepayPlan({ orderId: this.orderId });
        if (res?.code !== 'Success') {
          throw new Error(res?.message || 'repay plan request failed');
        }
        this.repayPlan = res.data || { planList: [] };
      } catch (error) {
        this.repayPlan = null;
        this.repayPlanError = true;
        console.error('Get repay plan failed:', error);
      } finally {
        this.repayPlanLoading = false;
      }
    },
    createRepayPlanPreview() {
      const term = Math.max(Number(this.showData?.rentalTerm) || 0, 0);
      const cycle = this.normalizeOrderStatus(this.showData?.planUnit);
      const rawStart = this.orderDetail?.createTime || this.orderDetail?.rentDate || Date.now();
      const numericStart = Number(rawStart);
      const startValue = Number.isFinite(numericStart) && numericStart > 0
        ? (numericStart < 100000000000 ? numericStart * 1000 : numericStart)
        : rawStart;
      const start = this.$dayjs(startValue).add(1, 'day');
      const firstAmount = Number(this.showData?.firstRentalAmount || 0);
      const planPrice = Number(this.showData?.planPrice || 0);
      const list = Array.from({ length: term }, (_, index) => {
        const date = this.repayPlanDate(start, cycle, index);
        return {
          period: index + 1,
          paidTime: date.valueOf(),
          total: index === 0 ? firstAmount : planPrice,
          rentalAmount: index === 0 ? firstAmount : planPrice,
          status: index === 0 && String(this.orderDetail?.payStatus || '').toUpperCase() === 'SUCCESS' ? 'SUCCESS' : 'PENDING'
        };
      });
      return { depositAmount: Number(this.showData?.depositAmount || 0), planList: list };
    },
    repayPlanStatusClass(status) {
      const normalized = this.normalizeOrderStatus(status);
      if (['SETTLED', 'PAID', 'SUCCESS'].includes(normalized)) return 'paid';
      if (normalized === 'OVERDUE') return 'overdue';
      if (['PENDING', 'UNPAID'].includes(normalized)) return 'unpaid';
      return 'upcoming';
    },
    repayPlanStatusText(status) {
      const statusClass = this.repayPlanStatusClass(status);
      if (statusClass === 'unpaid') return this.$t('rentalplan.unpaid');
      if (statusClass === 'upcoming') return this.$t('planDetail.status.upcoming');
      return this.$t(`planDetail.status.${statusClass}`);
    },
    repayPlanStatusIcon(status) {
      const statusClass = this.repayPlanStatusClass(status);
      if (statusClass === 'paid') return 'checked';
      if (statusClass === 'overdue') return 'warning-o';
      if (statusClass === 'unpaid') return 'circle';
      return 'clock-o';
    },
    repayPlanDate(start, cycle, offset) {
      if (offset <= 0) return start;
      if (['MONTH', 'MONTHLY', 'M'].includes(cycle)) {
        const repayDay = start.date() <= 20 ? 1 : 5;
        const nextMonthlyRepayDate = base => {
          let candidate = base.startOf('day').date(repayDay);
          if (!candidate.isAfter(base)) candidate = candidate.add(1, 'month').date(repayDay);
          return candidate;
        };
        let secondPeriodStart = nextMonthlyRepayDate(start);
        const graceEnd = start.add(10, 'day');
        if (graceEnd.isAfter(secondPeriodStart)) secondPeriodStart = nextMonthlyRepayDate(secondPeriodStart);
        return secondPeriodStart.add(offset - 1, 'month');
      }
      if (['BI_WEEK', 'BIWEEKLY', 'BI-WEEKLY', 'BI_WEEKLY', 'B'].includes(cycle)) return start.add(offset * 14, 'day');
      if (['WEEK', 'WEEKLY', 'W'].includes(cycle)) return start.add(offset * 7, 'day');
      if (['DAY', 'DAILY', 'D'].includes(cycle)) return start.add(offset, 'day');
      return start.add(offset * 28, 'day');
    },
    goTerminateFromRepayPlan() {
      if (!this.canApplyReturnFromRepayPlan || !this.orderId) return;
      if (String(this.orderDetail?.breachOrderStatus || '').trim() !== '') {
        this.repayPlanPopupVisible = false;
        this.$router.push({ name: 'Terminate', query: { orderId: this.orderId } });
        return;
      }
      queryBreachTrial({
        rentalOrderId: this.orderId
      }).then(res => {
        if (res?.code === 'Success' && res.data?.allowBreach) {
          this.repayPlanPopupVisible = false;
          this.$router.push({
            name: 'Terminate',
            query: {
              orderId: this.orderId,
              penaltyFee: res.data.penaltyFee || 0
            }
          });
        } else {
          this.$toast({
            message: res?.data?.reason || res?.message || this.$t('systemError'),
            duration: 3000
          });
        }
      }).catch(() => {
        this.$toast({
          message: this.$t('systemError'),
          duration: 3000
        });
      });
    },
    loadAutoDebitConfig() {
      if (this.autoDebitConfigPromise) return this.autoDebitConfigPromise;
      this.autoDebitConfigPromise = getAppSystemConfig({
        platform: 'h5',
        channel: this.$store.state.user.channel || DEFAULT_REG_CHANNEL
      })
        .then((res) => {
          if (res?.code !== 'Success') return false;
          const features = res.data?.features || res.data || {};
          this.autoDebitEnabled = [true, 'true', 1, '1'].includes(features.autoDebit);
          return this.autoDebitEnabled;
        })
        .catch(() => false);
      return this.autoDebitConfigPromise;
    },
    async maybeShowWithholdingAuthPopup() {
      if (!this.orderId) return;
      const orderId = this.orderId;
      if (!(await this.loadAutoDebitConfig())) return;
      // 前提：订单支付成功
      if (this.orderId !== orderId || !this.isPaidOrder) return;
      if (hasWithholdingAuthShown(this.orderId)) return;
      // 先检查授权状态；若当前未授权，先进行一次查询再进入弹窗流程
      if (!this.$store.state.user.userInfo.hasWithholdingAuthorization) {
        if (!this.memberInfoFetched) {
          try {
            const res = await this.$store.dispatch('user/fetchMemberInfo', { _showLoading: false });
            if (!res || res.code !== 'Success') return;
            this.memberInfoFetched = true;
          } catch (e) {
            return;
          }
        }
      }
      if (this.orderId !== orderId || !this.isPaidOrder) return;
      if (this.$store.state.user.userInfo.hasWithholdingAuthorization || hasWithholdingAuthShown(orderId)) return;
      markWithholdingAuthShown(orderId);
      this.showWithholdingAuthPopup = true;
    },
    async onWithholdingAuthAuthorize() {
      if (this.directDebitAuthorizing) return;
      this.directDebitAuthorizing = true;
      try {
        const res = await applyDirectDebit({ route: this.$route });
        if (res && res.code === 'Success' && res.data && res.data.authUrl) {
          this.showWithholdingAuthPopup = false;
          window.location.href = res.data.authUrl;
          return;
        }
        this.$toast({ message: res?.message || this.$t('systemError'), duration: 3000 });
      } catch (e) {
        this.$toast({ message: this.$t('systemError'), duration: 3000 });
      } finally {
        this.directDebitAuthorizing = false;
      }
    },
    async getOrderDetail({ silent = false } = {}) {
      this.detailLoading = !this.orderDetail;
      this.detailLoadError = false;
      try {
        const res = await queryOrderDetail({ orderId: this.orderId });
        if (res.code === 'Err.ErrOrderIllegal') {
          this.$toast({
            message: res.message,
            duration: 3000
          });
          setTimeout(() => {
            this.$router.replace({ name: 'Index' });
          }, 3000);
          return res;
        }
        if (res.code === 'Success') {
          this.orderDetail = res.data;
          this.maybeShowWithholdingAuthPopup();
          this.startPaymentExpireTimer();
          // 显示支付弹窗；payContent 优先使用二维码，只有 payContent 为空时才使用 payUrl。
          if (!silent && !this.isCod && this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.PAYMENT && !this.isPaymentExpired && String(this.orderDetail?.payContent || '').trim()) {
            const amount = this.orderDetail?.firstPayAmount || '';
            this.$refs.paymentPopup.open({
              payContent: this.orderDetail.payContent,
              amount,
              createTime: this.orderDetail.createTimeTs || this.orderDetail.createTime
            });
            this.stopPolling();
            this.paymentPopupVisible = true;
          } else if (!silent && !this.isCod && this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.PAYMENT && !this.isPaymentExpired && String(this.orderDetail?.payUrl || '').trim()) {
            this.openPayUrlPopup();
          }
          // payUrl 弹框内由用户主动检查支付结果，打开期间不再轮询。
          if (!this.isCod && this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.PAYMENT && !this.isPaymentExpired && !this.showPayUrlPopup && !this.paymentPopupVisible) {
            this.startPolling();
          } else {
            this.stopPolling();
          }
        } else if (!this.orderDetail) {
          this.detailLoadError = true;
        }
        return res;
      } catch (e) {
        console.error('Get order detail failed:', e);
        if (!this.orderDetail) this.detailLoadError = true;
        if (!this.orderDetail) this.$toast(this.$t('systemError'));
        return null;
      } finally {
        this.detailLoading = false;
      }
    },
    // 启动轮询
    startPolling() {
      // 防止重复启动轮询
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
      }

      this.currentPollingAttempts = 0; // 重置尝试次数

      this.pollingInterval = setInterval(() => {
        this.pollOrderStatus();
      }, this.pollingDelay);
    },

    // 轮询订单状态
    pollOrderStatus() {
      if (this.isPaymentExpired) {
        this.handlePaymentExpired();
        return;
      }
      if (this.currentPollingAttempts >= this.maxPollingAttempts) {
        this.stopPolling();
        return;
      }

      this.currentPollingAttempts++;

      queryOrderDetail({
        orderId: this.orderId
      }).then(res => {
        if (res.code === 'Err.ErrOrderIllegal') {
          this.$toast({
            message: res.message,
            duration: 3000
          });
          setTimeout(() => {
            this.$router.replace({ name: 'Index' });
          }, 3000);
          this.stopPolling();
          return;
        }
        if (res.code === 'Success') {
          const oldStatus = this.normalizeOrderStatus(this.orderDetail?.status);
          this.orderDetail = res.data;
          this.maybeShowWithholdingAuthPopup();
          this.startPaymentExpireTimer();
          if (this.isPaymentExpired) {
            this.handlePaymentExpired();
            return;
          }

          // 检查订单状态是否已改变
          if (oldStatus !== this.normalizeOrderStatus(this.orderDetail?.status)) {
            // 如果订单状态不再是待支付状态，停止轮询
            if (this.normalizeOrderStatus(this.orderDetail.status) !== ORDER_STATUS.PAYMENT) {
              this.stopPolling();
              // 支付成功或订单状态变化后，主动关闭支付二维码弹窗
              if (this.$refs.paymentPopup) {
                this.$refs.paymentPopup.close();
              }
            }
            // 重新计算显示数据
            this.$forceUpdate();
          }
        }
      }).catch(err => {
        console.error('轮询订单状态失败:', err);
      });
    },

    // 停止轮询
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    startPaymentExpireTimer() {
      this.stopPaymentExpireTimer();
      const createTime = this.orderDetail?.createTimeTs || this.orderDetail?.createTime;
      if (!createTime || this.normalizeOrderStatus(this.orderDetail?.status) !== ORDER_STATUS.PAYMENT || this.isCod) {
        return;
      }
      this.paymentCountdown = getPaymentRemainingSeconds(createTime);
      if (this.paymentCountdown <= 0) {
        this.currentTime = Date.now();
        this.updatePaymentCountdownText();
        this.handlePaymentExpired();
        return;
      }
      this.paymentCountdownEndTime = Date.now() + this.paymentCountdown * 1000;
      this.updatePaymentCountdownText();
      this.schedulePaymentCountdownTick();
    },
    schedulePaymentCountdownTick() {
      const remaining = Math.max(0, Math.floor((this.paymentCountdownEndTime - Date.now()) / 1000));
      if (this.paymentCountdown !== remaining) {
        this.paymentCountdown = remaining;
        this.updatePaymentCountdownText();
      }
      if (remaining <= 0) {
        this.paymentCountdown = 0;
        this.updatePaymentCountdownText();
        this.currentTime = Date.now();
        this.handlePaymentExpired();
        return;
      }
      const now = Date.now();
      const nextMs = 1000 - (now % 1000) + 1;
      this.paymentExpireTimer = setTimeout(() => this.schedulePaymentCountdownTick(), nextMs);
    },
    updatePaymentCountdownText() {
      const totalSeconds = Math.max(Number(this.paymentCountdown) || 0, 0);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      this.paymentCountdownText = [hours, minutes, seconds]
        .map(value => String(value).padStart(2, '0'))
        .join(':');
    },
    stopPaymentExpireTimer() {
      if (this.paymentExpireTimer) {
        clearTimeout(this.paymentExpireTimer);
        this.paymentExpireTimer = null;
      }
    },
    handlePaymentExpired() {
      this.currentTime = Date.now();
      this.stopPolling();
      this.stopPaymentExpireTimer();
      if (this.$refs.paymentPopup) {
        this.$refs.paymentPopup.close();
      }
      this.showPayUrlPopup = false;
    },
    onClickLeft() {
      this.goBack()
    },
    toggleSection(name) {
      this.$set(this.expandedSections, name, !this.expandedSections[name]);
    },
    async copyOrderNumber() {
      const orderNumber = this.showData?.orderId;
      if (!orderNumber) return;
      const success = await copyText(orderNumber);
      this.$toast({
        message: this.$t(success ? 'orderPage.copySuccess' : 'orderPage.copyFailed'),
        duration: 1500
      });
    },
    buildCustomerSupportUrl() {
      const channel = this.$store.state.user.channel || DEFAULT_REG_CHANNEL;
      return buildCustomerSupportWhatsAppUrl(channel);
    },
    openCustomerSupport() {
      window.open(this.buildCustomerSupportUrl(), '_blank', 'noopener,noreferrer');
    },
    backToOrders() {
      this.$router.replace({ name: 'OrderList' });
    },
    onCancelClick() {
      if (this.isDelivery || this.isStoreApprovedPaidAwaitingShipment) {
        this.cancelReason = ''
        this.refundType = ''
        this.selectedBankId = ''
        this.selectedBankName = ''
        this.refundAccountNo = ''
        this.refundAccountName = ''
        if (this.showCancelRefundFields) {
          this.loadBankList()
        }
        this.showCancelDialog = true
        return
      }
      Dialog.confirm({
        title: this.$t('orderList.cancelTitle'),
        message: this.$t('orderList.cancelContent'),
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel'),
      }).then(() => {
        this.doCancelOrder({ orderId: this.orderDetail.orderId })
      }).catch(() => {})
    },
    onCancelConfirm() {
      this.showCancelDialog = false
      const params = { orderId: this.orderDetail.orderId, reason: this.cancelReason }
      if (this.showCancelRefundFields) {
        if (!this.refundType) {
          this.$toast(this.$t('orderList.refundTypeEmpty'))
          this.showCancelDialog = true
          return
        }
        if (!this.selectedBankId) {
          this.$toast(this.$t('orderList.bankEmpty'))
          this.showCancelDialog = true
          return
        }
        if (!this.refundAccountNo) {
          this.$toast(this.$t('orderList.accountNoEmpty'))
          this.showCancelDialog = true
          return
        }
        if (!this.refundAccountName) {
          this.$toast(this.$t('orderList.accountNameEmpty'))
          this.showCancelDialog = true
          return
        }
        if (!this.validateAccountNo(this.refundAccountNo, this.refundType)) {
          this.$toast(this.$t('orderList.accountNoError'))
          this.showCancelDialog = true
          return
        }
        if (!/^[a-zA-Z0-9,\.'\-\s]+$/.test(this.refundAccountName)) {
          this.$toast(this.$t('orderList.accountNameError'))
          this.showCancelDialog = true
          return
        }
        Object.assign(params, {
          refundType: this.refundType,
          refundBankId: this.selectedBankId,
          refundBankName: this.selectedBankName,
          accountNo: this.refundAccountNo,
          accountName: this.refundAccountName,
        })
      }
      this.doCancelOrder(params)
    },
    doCancelOrder(params) {
      if (this.actionSubmitting) return;
      this.actionSubmitting = true;
      cancelOrder(params).then(res => {
        if (res && res.code === 'Success') {
          this.$toast({ message: this.$t('orderList.cancelSuccess'), duration: 2000 })
          this.getOrderDetail()
        } else {
          this.$toast({ message: res?.message || this.$t('orderList.cancelFailed'), duration: 3000 })
        }
      }).catch(() => {
        this.$toast({ message: this.$t('orderList.cancelFailed'), duration: 3000 })
      }).finally(() => {
        this.actionSubmitting = false;
      })
    },
    detectDataKeys(data) {
      const item = data && data[0]
      if (!item) return
      const nameKeys = ['bank_name', 'bankName', 'name', 'label', 'text']
      for (const key of nameKeys) {
        if (item[key] !== undefined) {
          this.bankValueKey = key
          break
        }
      }
    },
    validateAccountNo(accountNo, type) {
      if (type === 'VA') return /^\d{12,28}$/.test(accountNo)
      if (type === 'BANK') return /^\d{1,16}$/.test(accountNo)
      if (!/^\d{8,19}$/.test(accountNo)) return false
      return /^(8|08|628|6208)/.test(accountNo)
    },
    loadBankList() {
      if (this.bankListLoaded) return
      Promise.all([
        queryBankList({ type: 1 }),
        queryBankList({ type: 2 })
      ]).then(([res1, res2]) => {
        if (res1?.code === 'Success') {
          this.bankListType1 = res1.data || []
          if (this.bankListType1.length) this.detectDataKeys(this.bankListType1)
        }
        if (res2?.code === 'Success') {
          this.bankListType2 = res2.data || []
          if (this.bankListType2.length && !this.bankValueKey) this.detectDataKeys(this.bankListType2)
        }
        this.bankListLoaded = true
      }).catch(e => {
        console.error('Bank list load failed', e)
      })
    },
    openBankPicker() {
      this.$refs.bankPicker.open()
    },
    onBankPickerConfirm(value) {
      const item = Array.isArray(value) ? value[0] : value
      if (!item) return
      const idKeys = ['bank_id', 'id', 'bankId', 'code']
      for (const key of idKeys) {
        if (item[key] !== undefined) {
          this.selectedBankId = item[key]
          break
        }
      }
      this.selectedBankName = item[this.bankValueKey] || ''
    },
    onLogisticsClick() {
      const orderId = this.orderDetail.orderId
      if (this.express && this.express.orderId === orderId) {
        this.$refs.deliveryProgress.open()
        return
      }
      queryExpressByOrderId({ orderId }).then(res => {
        if (res?.code === 'Success') {
          this.express = { orderId, ...res.data }
          this.$refs.deliveryProgress.open()
        }else {
          this.$toast({
            message: res?.message || this.$t('systemError'),
          })
        }
      }).catch((err) => {
        this.$toast({
          message: err?.message || this.$t('systemError'),
        })
      })
    },
    onWithdrawCancel() {
      this.showWithdrawDialog = true
    },
    doWithdrawCancel() {
      if (this.actionSubmitting) return;
      this.actionSubmitting = true;
      const orderId = this.orderDetail.orderId
      withdrawCancelRefund({ orderId }).then(res => {
        if (res && res.code === 'Success') {
          this.$toast({ message: this.$t('orderList.cancelStatus.withdrawSuccess'), duration: 2000 })
          this.showWithdrawDialog = false
          this.getOrderDetail()
        } else {
          this.$toast({ message: res?.message || this.$t('systemError'), duration: 3000 })
        }
      }).catch(() => {
        this.$toast({ message: this.$t('systemError'), duration: 3000 })
      }).finally(() => {
        this.actionSubmitting = false;
      })
    },
    openPaymentPopup() {
      if (this.isPaymentExpired) {
        this.$toast({ message: this.$t('orderPage.orderExpired'), duration: 3000 });
        return;
      }
      if (this.orderDetail?.payContent) {
        this.stopPolling();
        this.paymentPopupVisible = true;
        const amount = this.orderDetail?.firstPayAmount || '';
        this.$refs.paymentPopup.open({
          payContent: this.orderDetail.payContent,
          amount,
          createTime: this.orderDetail?.createTimeTs || this.orderDetail?.createTime
        });
      } else if (this.orderDetail?.payUrl) {
        this.openPayUrlPopup();
      }
    },
    openPayUrlPopup() {
      if (!this.orderDetail?.payUrl || this.isPaymentExpired) {
        return;
      }
      this.stopPolling();
      this.showPayUrlPopup = true;
    },
    onPaymentPopupClose() {
      this.paymentPopupVisible = false;
      if (!this.isCod && this.normalizeOrderStatus(this.orderDetail?.status) === ORDER_STATUS.PAYMENT && !this.isPaymentExpired && !this.showPayUrlPopup) {
        this.startPolling();
      }
    },
    goToPayUrl() {
      if (!this.orderDetail?.payUrl) {
        return;
      }
      window.open(this.orderDetail.payUrl, '_blank', 'noopener,noreferrer');
    },
    async checkPayUrlPaymentStatus() {
      if (this.checkingPaymentStatus) {
        return;
      }
      this.checkingPaymentStatus = true;
      try {
        const res = await queryOrderDetail({ orderId: this.orderId });
        if (res?.code === 'Err.ErrOrderIllegal') {
          this.$toast({ message: res.message, duration: 3000 });
          this.stopPolling();
          setTimeout(() => {
            this.$router.replace({ name: 'Index' });
          }, 3000);
          return;
        }
        if (res?.code !== 'Success') {
          this.$toast({ message: res?.message || this.$t('systemError'), duration: 3000 });
          return;
        }

        this.orderDetail = res.data;
        this.startPaymentExpireTimer();
        if (this.normalizeOrderStatus(this.orderDetail?.status) !== ORDER_STATUS.PAYMENT) {
          this.showPayUrlPopup = false;
          this.stopPolling();
          return;
        }
        if (this.isPaymentExpired) {
          this.handlePaymentExpired();
          return;
        }
        this.$toast({ message: this.$t('orderPage.paymentNotCompleted'), duration: 3000 });
      } catch (e) {
        this.$toast({ message: this.$t('systemError'), duration: 3000 });
      } finally {
        this.checkingPaymentStatus = false;
      }
    },
    goCodShippingFeeCheckout() {
      if (!this.showCodShippingFeePayAction) {
        return;
      }
      this.$router.push({
        name: 'CodShippingFeeCheckout',
        query: { orderId: this.orderId }
      });
    },
    goStorePaymentCheckout() {
      if (!this.showStorePayAtStoreAction) return;
      this.$router.push({ name: 'StorePaymentCheckout', query: { orderId: this.orderId } });
    },
  },
  // 页面销毁时清理轮询
  beforeDestroy() {
    this.storeRequestVersion++;
    this.stopPolling();
    this.stopPaymentExpireTimer();
  },

  // 页面离开时也停止轮询
  beforeRouteLeave(to, from, next) {
    this.stopPolling();
    this.stopPaymentExpireTimer();
    next();
  },
  beforeRouteUpdate(to, from, next) {
    const orderId = this.resolveOrderIdFromRoute(to);
    if (!orderId) {
      next('/order/confirm');
      return;
    }
    const previousOrderId = this.orderId;
    this.orderId = orderId;
    this.scrollToTop();
    next();
    this.$nextTick(() => {
      this.cleanThirdPartyReturnUrl(to, orderId);
      if (previousOrderId !== orderId) {
        this.getOrderDetail();
        this.queryAdditionalInfoStatus();
      }
    });
  },
}
</script>
<style lang="less">
.order-state {
  min-height: calc(100vh - 88px);
  padding-top: 180px;
  box-sizing: border-box;
  color: var(--text-color-gray);
  font-size: 28px;
  text-align: center;

  .btn-primary {
    margin: 28px auto 0;
    width: 220px;
  }

  &__back {
    margin-top: 24px;
    color: var(--primary-color);
  }
}

.lease {
  font-size: 26px;
  color: var(--text-color-gray);
  line-height: 38px;
  text-align: left;

  .fb {
    font-weight: 600;
  }

  p > span {
    font-weight: 600;
  }
}

.android-withdraw-dialog {
  width: 650px;
  max-width: calc(100vw - 64px);
  border-radius: 32px;
  overflow: hidden;

  /deep/ .van-dialog__content {
    padding: 0;
  }
}

.android-withdraw-dialog__content {
  position: relative;
  box-sizing: border-box;
  padding: 48px 48px 40px;
  background: #FFFFFF;
  text-align: center;
}

.android-withdraw-dialog__scene {
  position: relative;
  width: 128px;
  height: 128px;
  margin: 0 auto;
}

.android-withdraw-dialog__halo,
.android-withdraw-dialog__icon {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.android-withdraw-dialog__halo {
  width: 88px;
  height: 88px;
  border-radius: 24px;
  background: rgba(255, 45, 22, .14);
}

.android-withdraw-dialog__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 18px;
  color: #FFFFFF;
  background: #FF2D16;
  font-size: 40px;
}

.android-withdraw-dialog__title {
  margin-top: 24px;
  color: #070D3F;
  font-size: 34px;
  line-height: 44px;
  font-weight: 700;
}

.android-withdraw-dialog__message {
  margin-top: 12px;
  padding: 0 24px;
  color: #6B7280;
  font-size: 26px;
  line-height: 36px;
  font-weight: 500;
}

.android-withdraw-dialog__actions {
  display: flex;
  gap: 24px;
  margin-top: 40px;
}

.android-withdraw-dialog__button {
  flex: 1;
  height: 88px;
  padding: 0 16px;
  border-radius: 16px;
  font-size: 28px;
  line-height: 88px;
  font-weight: 500;
  text-align: center;
}

.android-withdraw-dialog__button--cancel {
  border: 2px solid #DDE5F2;
  color: #070D3F;
  background: #FFFFFF;
}

.android-withdraw-dialog__button--confirm {
  border: 0;
  color: #FFFFFF;
  background: #FF2D16;
}

.android-withdraw-dialog__button:disabled {
  opacity: .55;
}

.pay-url-popup {
  position: relative;
  padding: 48px 40px 44px;
  box-sizing: border-box;
  text-align: center;

  &__close {
    position: absolute;
    top: 24px;
    right: 24px;
    width: 36px;
    height: 36px;

    img {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__title {
    font-size: 34px;
    line-height: 48px;
    font-weight: 600;
    color: var(--text-color-black);
  }

  &__desc {
    margin-top: 20px;
    font-size: 26px;
    line-height: 38px;
    color: var(--text-color-gray);
  }

  &__footer {
    margin-top: 40px;
  }

  &__completed-btn {
    height: 80px;
    margin-top: 20px;
    border: 2px solid var(--primary-color);
    border-radius: var(--border-radius-base);
    box-sizing: border-box;
    font-size: 28px;
    line-height: 76px;
    color: var(--primary-color);

    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }
}
</style>

<style lang="less" scoped>
.android-order-fold {
  &__collapse {
    display: grid;
    grid-template-rows: 0fr;
    visibility: hidden;
    transition: grid-template-rows 240ms ease, visibility 0s linear 240ms;
    &--expanded {
      grid-template-rows: 1fr;
      visibility: visible;
      transition: grid-template-rows 240ms ease, visibility 0s;
    }
  }
  &__inner { min-height: 0; overflow: hidden; }
  &__arrow { transition: transform 240ms ease; }
  &__arrow--expanded { transform: rotate(180deg); }
  @media (prefers-reduced-motion: reduce) {
    &__collapse, &__collapse--expanded, &__arrow { transition: none; }
  }
}

.order-store-card {
  .android-order-section__heading .van-icon { color: #102BDB; }
  .order-store-card__name { font-weight: 600; white-space: normal; overflow-wrap: anywhere; }
  .order-store-card__phone { display: block; color: #1664F5; font-size: 30px; line-height: 40px; text-decoration: none; white-space: normal; overflow-wrap: anywhere; }
  &__hours { margin-top: 4px; color: #64748B; font-size: 26px; line-height: 36px; overflow-wrap: anywhere; }
  &__state {
    padding: 24px 0; color: #65708E; font-size: 26px; text-align: center;
    &--error {
      color: #E54747;
      button { margin-left: 16px; border: 0; background: transparent; color: #1664F5; font-size: 25px; }
    }
  }
}

.order-detail {
  padding-top: 46px;
  background: var(--bg-color);

  ::v-deep .van-nav-bar {
    height: 46px;
    background: #fff;
  }

  ::v-deep .van-nav-bar__title {
    color: #172554;
    font-size: 15px;
    font-weight: 600;
  }

  ::v-deep .van-nav-bar .van-icon {
    color: #172554;
    font-size: 20px;
  }
}

.android-order-detail {
  min-height: calc(100vh - 46px);
  padding: 8px 8px calc(1.88rem + env(safe-area-inset-bottom));
  box-sizing: border-box;
  color: #172554;
  background: var(--bg-color);
  font-size: 13px;
  line-height: 18px;
}

.android-order-status-card,
.android-order-cancel-card,
.android-order-section,
.android-order-note-card,
.android-order-additional-card {
  margin-bottom: 8px;
  border: 1px solid #E5EAF3;
  border-radius: 10px;
  background: #fff;
  box-sizing: border-box;
}

.android-order-status-card {
  padding: 8px;
  background: #EEF5FF;

  &__top {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 20px;
    width: 20px;
    height: 20px;
    color: #fff;
    font-size: 15px;
    border-radius: 50%;
    background: var(--android-order-status-tone, #3155A5);
  }

  &__copy {
    min-width: 0;
    flex: 1;
  }

  &__title {
    color: #172554;
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;
  }

  &__description {
    color: #64748B;
    font-size: 12px;
    line-height: 16px;
  }

  &__list-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: #64748B;
    font-size: 12px;
    line-height: 16px;
  }

  &__list-text {
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  &__bullet {
    flex: 0 0 4px;
    width: 4px;
    height: 4px;
    margin-top: 6px;
    border-radius: 999px;
    background: #64748B;
  }

  &__body {
    margin-top: 8px;
    padding: 8px 0 0;
    border-top: 1px solid #DCE6F3;
  }

  &__cod-pay {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 26px;
    margin-top: 8px;
    padding: 0 8px;
    border: 1px solid #FFD79A;
    border-radius: 4px;
    box-sizing: border-box;
    color: #FF7A00;
    background: #FFF7E8;
    font-size: 11px;
    line-height: 16px;
    font-weight: 400;
    letter-spacing: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &--disabled {
      opacity: .6;
      pointer-events: none;
    }
  }

  &__store-pay {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 26px;
    margin-top: 8px;
    padding: 0 8px;
    border: 1px solid #FFD79A;
    border-radius: 4px;
    box-sizing: border-box;
    color: #FF7A00;
    background: #FFF7E8;
    font-size: 11px;
    line-height: 16px;
    font-weight: 400;
    letter-spacing: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__cod-paid {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
    width: 100%;
    min-height: 26px;
    margin-top: 8px;
    padding: 5px 16px;
    border: 1px solid #FFD79A;
    border-radius: 4px;
    box-sizing: border-box;
    color: #FF9A16;
    background: #FFF7E8;
    font-size: 11px;
    line-height: 16px;

    span {
      flex: 0 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    strong {
      flex: 0 0 auto;
      font-weight: 600;
    }
  }

  &__badge {
    flex: 0 0 auto;
    max-width: 42%;
    padding: 3px 8px;
    border: 1px solid var(--android-order-status-border, rgba(49, 85, 165, .35));
    border-radius: 8px;
    color: var(--android-order-status-tone, #3155A5);
    background: rgba(255, 255, 255, .78);
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }

  &__meta,
  &__deadline {
    margin-top: 8px;
    color: var(--android-order-status-tone, #3155A5);
    font-size: 12px;
    line-height: 16px;
  }

  &__pending {
    margin-top: 8px;
    padding: 8px 0 0;
    border-top: 1px solid #DCE6F3;
  }

  &__pending-desc {
    color: #64748B;
    font-size: 12px;
    line-height: 16px;
  }

  &__pending-countdown {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    margin-top: 8px;
    color: #FF8A00;
    font-size: 16px;
    line-height: 20px;
    font-weight: 700;

    .van-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      color: inherit;
      font-size: 15px;
      line-height: 1;
    }
  }

  &__time {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 8px;
    color: var(--android-order-status-tone, #2563EB);
    font-size: 12px;
    line-height: 16px;

    .van-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      color: inherit;
      font-size: 15px;
      line-height: 1;
    }
  }

  &__deadline {
    display: flex;
    justify-content: space-between;
    gap: 8px;
  }

  &__link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #DCE6F3;
    color: #3155A5;
    font-size: 13px;
  }
}

.android-order-status-card.status-orange {
  --android-order-status-tone: #FF8A00;
  --android-order-status-border: rgba(255, 138, 0, .35);
  border-color: #F4D2A8;
  background: #FFF9F0;

  .android-order-status-card__title {
    font-weight: 400;
  }
}

.android-order-status-card.status-blue {
  --android-order-status-tone: #2563EB;
  --android-order-status-border: rgba(37, 99, 235, .35);
  border-color: #CFE0FF;
  background: #EEF5FF;
}

.android-order-status-card.status-green {
  --android-order-status-tone: #22C55E;
  --android-order-status-border: rgba(34, 197, 94, .35);
  border-color: #CFE8D6;
  background: #EAFBF0;
}

.android-order-status-card.status-gray {
  --android-order-status-tone: #64748B;
  --android-order-status-border: rgba(100, 116, 139, .35);
  border-color: #E5E7EB;
  background: #F4F5F7;
}

.android-order-status-card.status-cyan {
  --android-order-status-tone: #0891B2;
  --android-order-status-border: rgba(8, 145, 178, .35);
  border-color: #BFE8E2;
  background: #F0FDFA;
}

.android-order-status-card.status-purple {
  --android-order-status-tone: #7C3AED;
  --android-order-status-border: rgba(124, 58, 237, .35);
  border-color: #DCCCF5;
  background: #F7F0FF;
}

.android-order-action {
  display: block;
  width: 100%;
  height: 46px;
  margin-top: 8px;
  border: 0;
  border-radius: 8px;
  font-size: 13px;
  line-height: 44px;
  font-weight: 600;

  &--primary {
    color: #fff;
    background: var(--primary-color);
  }

  &:disabled {
    opacity: .5;
  }
}

.android-order-cancel-card {
  display: flex;
  gap: 8px;
  padding: 8px;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    border-radius: 8px;
    color: #64748B;
    background: #F1F5F9;
    font-size: 18px;
  }

  &__body {
    min-width: 0;
    flex: 1;
  }

  &__title {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px;
    color: #172554;
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;
  }

  &__inline-status {
    color: inherit;
    font-size: 13px;
    line-height: 18px;
    font-weight: 500;
  }

  &__list {
    margin-top: 6px;
  }

  &__list-item {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: 4px;
    color: #64748B;
    font-size: 12px;
    line-height: 16px;
  }

  &__bullet {
    flex: 0 0 5px;
    width: 5px;
    height: 5px;
    margin-top: 5px;
    border-radius: 50%;
    background: currentColor;
    opacity: .75;
  }

  &__list-text {
    min-width: 0;
    flex: 1;
    word-break: break-word;
  }

  &__action {
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;

    .android-order-text-button {
      margin-top: 0;
    }
  }

  &--pending {
    border-color: #CFE8D6;
    background: #F5FCF6;
  }

  &--pending &__icon,
  &--pending &__title {
    color: #15803D;
  }

  &--pending &__icon {
    background: #E7F8EE;
  }

  &--rejected {
    border-color: #F1C7C7;
    background: #FFF7F7;
  }

  &--rejected &__icon,
  &--rejected &__title {
    color: #DC2626;
  }

  &--rejected &__icon {
    background: #FFE4E4;
  }
}

.android-order-text-button,
.android-order-copy {
  padding: 0;
  border: 0;
  background: transparent;
  color: #3155A5;
  font-size: 12px;
  line-height: 16px;
}

.android-order-text-button {
  margin-top: 8px;
}

.android-order-cancel-card__action .android-order-text-button {
  min-width: 100px;
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  color: #FFFFFF;
  background: #EF4444;
  font-size: 13px;
  line-height: 36px;
  text-align: center;
}

.android-order-section {
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 38px;
    padding: 8px;
    border: 0;
    background: #fff;
    color: #172554;
    font: inherit;
    text-align: left;
  }

  &__header--static {
    cursor: default;
  }

  &__heading {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #172554;
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;

    .van-icon,
    .android-order-icon {
      flex: 0 0 18px;
      color: #3155A5;
      font-size: 18px;
    }
  }

  &__header > .van-icon {
    color: #64748B;
    font-size: 16px;
  }

  &__body {
    padding: 8px;
    border-top: 1px solid #E5EAF3;
  }
}

.android-order-address__name,
.android-order-contact,
.android-order-product__name,
.android-order-price-row {
  color: #172554;
  font-size: 13px;
  line-height: 18px;
}

.android-order-address {
  min-width: 0;
}

.android-order-address__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 400;
}

.android-order-address__phone,
.android-order-address__detail {
  margin-top: 4px;
  overflow: hidden;
  color: #172554;
  font-size: 13px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.android-order-address__detail {
  white-space: normal;
  word-break: break-word;
}

.android-order-address__detail:not(.android-order-address__detail--expanded) {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
}

.android-order-muted {
  margin-top: 4px;
  color: #64748B;
  font-size: 12px;
  line-height: 16px;
  word-break: break-word;
}

.android-order-contact {
  display: block;
  padding: 4px 0;

  &__identity {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__name,
  &__relationship {
    color: #172554;
    font-size: 13px;
    line-height: 18px;
    font-weight: 400;
  }

  &__phone {
    margin-top: 4px;
    color: #172554;
    font-size: 13px;
    line-height: 18px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__divider {
    height: 1px;
    margin-top: 10px;
    background: #E5EAF3;
  }
}

.android-order-product {
  display: flex;
  gap: 8px;

  &__image {
    flex: 0 0 72px;
    width: 72px;
    height: 72px;
    border: 0;
    border-radius: 8px;
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
  }

  &__info {
    min-width: 0;
    flex: 1;
  }

  &__name {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.android-order-number {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding-top: 0;
  color: #64748B;
  font-size: 12px;
  line-height: 16px;

  > span {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.android-order-copy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  font-size: 18px;
}

.android-order-product {
  margin-top: 8px;
}

.android-order-date-block {
  small {
    display: block;
    margin-top: 2px;
    color: #64748B;
    font-size: 12px;
    line-height: 16px;
  }
}

.android-order-date-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.android-order-date-block {
  min-width: 0;
  padding: 8px;
  border: 1px solid #E5EAF3;
  border-radius: 8px;

  span,
  strong {
    display: block;
  }

  span {
    color: #070D3F;
    font-size: 12px;
    line-height: 16px;
    font-weight: 400;
  }

  strong {
    margin-top: 4px;
    color: #0B45FF;
    font-size: 13px;
    line-height: 18px;
    font-weight: 400;
  }
}

.android-order-info-note {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 8px;
  color: #64748B;
  font-size: 12px;
  line-height: 16px;

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    margin-top: 2px;
    border-radius: 50%;
    color: #fff;
    background: #6B7280;
    font-family: Arial, sans-serif;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 28px;
  }

  &__text {
    min-width: 0;
    flex: 1;
  }

  .van-icon,
  .android-order-icon {
    flex: 0 0 auto;
    margin-top: 1px;
  }
}

.android-order-note-card {
  padding: 8px;

  &__title {
    color: #172554;
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;
  }

  &__text {
    margin-top: 4px;
    color: #64748B;
    font-size: 12px;
    line-height: 16px;
  }

  &--cod {
    border-color: #F4D2A8;
    background: #FFF9F0;

    .android-order-note-card__title {
      color: #F59E0B;
    }
  }

  &--cod-shipping-fee {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    padding: 10px 12px 12px;
    border-color: #FFD79A;

    .android-order-note-card__icon {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 26px;
      width: 26px;
      height: 26px;
      margin-top: 2px;
      border-radius: 6px;
      color: transparent;
      background: #FFECC7;
      font-family: Arial, sans-serif;
      font-size: 0;
      line-height: 1;

      &::before {
        content: '';
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: #FF9F1A;
      }

      &::after {
        content: 'i';
        position: absolute;
        left: 50%;
        top: 50%;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        line-height: 14px;
        transform: translate(-50%, -50%);
      }
    }

    .android-order-note-card__content {
      min-width: 0;
      flex: 1;
    }

    .android-order-note-card__title {
      color: #F59E0B;
      line-height: 20px;
    }

    .android-order-note-card__text {
      margin-top: 6px;
      color: #64748B;
      line-height: 18px;
    }
  }

  &--cod-reminder {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    padding: 10px 16px 12px;
    border-color: #FFD79A;

    .android-order-note-card__icon {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 26px;
      width: 26px;
      height: 26px;
      margin-top: 2px;
      border-radius: 6px;
      color: transparent;
      background: #FFECC7;
      font-family: Arial, sans-serif;
      font-size: 0;
      line-height: 1;

      &::before {
        content: '';
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: #FF9F1A;
      }

      &::after {
        content: 'i';
        position: absolute;
        left: 50%;
        top: 50%;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        line-height: 14px;
        transform: translate(-50%, -50%);
      }
    }

    .android-order-note-card__content {
      min-width: 0;
      flex: 1;
      max-width: none;
    }

    .android-order-note-card__title {
      line-height: 20px;
    }

    .android-order-note-card__text {
      margin-top: 4px;
      text-align: left;
    }

    .android-order-note-card__cod-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      margin-top: 4px;
      width: 100%;
    }

    .android-order-note-card__currency {
      flex: 0 0 auto;
      color: #0B45FF;
      font-size: 16px;
      line-height: 20px;
      font-weight: 400;
    }

    .android-order-note-card__cod-desc {
      min-width: 0;
      flex: 1;
      max-width: none;
      color: #64748B;
      font-size: 12px;
      line-height: 16px;
      text-align: left;
    }

    .android-order-note-card__amount {
      color: #0B45FF;
      font-weight: 400;
    }
  }

  &--store-reminder {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    padding: 10px 16px 12px;
    border-color: #FFD79A;
    background: #FFF4EC;

    .android-order-note-card__icon {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 26px;
      width: 26px;
      height: 26px;
      margin-top: 2px;
      border-radius: 6px;
      color: transparent;
      background: #FFECC7;
      font-family: Arial, sans-serif;
      font-size: 0;
      line-height: 1;

      &::before {
        content: '';
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: #FF9F1A;
      }

      &::after {
        content: 'i';
        position: absolute;
        left: 50%;
        top: 50%;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        line-height: 14px;
        transform: translate(-50%, -50%);
      }
    }

    .android-order-note-card__content {
      min-width: 0;
      flex: 1;
      max-width: none;
    }

    .android-order-note-card__title {
      color: #F59E0B;
      line-height: 20px;
    }

    .android-order-note-card__text {
      margin-top: 4px;
      text-align: left;
    }
  }

}

.android-order-price-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 0;
  text-align: right;

  span:first-child {
    min-width: 0;
    flex: 1 1 auto;
    color: #64748B;
    text-align: left;
  }

  span:last-child,
  strong {
    flex: 0 1 auto;
    max-width: 62%;
    word-break: break-word;
  }

  &--total {
    padding-top: 0;

    span:first-child,
    strong {
      color: #172554;
      font-weight: 600;
    }

    strong {
      color: #0B45FF;
    }
  }

  &--cod-total {
    span:first-child {
      color: #172554;
    }

    strong {
      color: #0B45FF;
    }
  }
}

.android-order-divider {
  height: 1px;
  margin: 4px 0;
  background: #E5EAF3;
}

.android-order-link-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 8px;
  gap: 8px;
  padding: 10px 14px;
  border: 1px solid #DDE5F2;
  border-radius: 8px;
  background: #fff;
  color: #3155A5;
  font-size: 13px;
  line-height: 18px;
  text-align: left;

  &__icon {
    flex: 0 0 18px;
    color: #112DDB;
    font-size: 18px;
  }

  > span {
    min-width: 0;
    flex: 1;
  }
}

.android-order-price-notices {
  display: grid;
  gap: 8px;
  margin-top: 8px;
}

.android-order-price-row {
  small {
    color: inherit;
    font-size: inherit;
    line-height: inherit;
  }
}

.android-repay-plan-popup {
  max-height: 88vh;
}

.android-repay-plan-sheet {
  padding: 16px 28px calc(16px + env(safe-area-inset-bottom));
  color: #172554;
}

.android-repay-plan-sheet__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 88px;
  border-bottom: 2px solid #E5EAF3;
}

.android-repay-plan-sheet__title {
  color: #070D3F;
  font-size: 32px;
  line-height: 44px;
  font-weight: 500;
}

.android-repay-plan-sheet__header > .van-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #F5F7FC;
  color: #64748B;
  font-size: 40px;
}

.android-repay-plan-sheet__return {
  margin-top: 16px;
  color: #0B45FF;
  font-size: 26px;
  line-height: 36px;
  text-align: right;
}

.android-repay-plan-sheet__body {
  max-height: 68vh;
  overflow-y: auto;
  padding-top: 24px;
}

.android-repay-plan-sheet__notice {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  padding: 20px;
  border-radius: 16px;
  background: #EAF3FF;

  &-icon {
    flex: 0 0 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin-top: 0;
    border-radius: 8px;
    background: #fff;
    color: #0B45FF;

    .van-icon,
    .android-order-icon {
      width: 30px;
      height: 30px;
      font-size: 30px;
    }
  }

  > div {
    min-width: 0;
    flex: 1;
  }

  > div > strong,
  > div > span {
    display: block;
  }

  > div > strong {
    color: #070D3F;
    font-size: 26px;
    line-height: 36px;
    font-weight: 500;
  }

  > div > span {
    margin-top: 4px;
    color: #64748B;
    font-size: 24px;
    line-height: 32px;
  }

  &--secure {
    margin-top: 16px;
    gap: 16px;
    background: #EAF3FF;

    .android-repay-plan-sheet__notice-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-basis: 40px;
      width: 40px;
      height: 40px;
      margin: 0;
    }

    > div {
      display: flex;
      align-items: center;
      height: 40px;
    }

    > div > strong {
      font-weight: 400;
      line-height: 40px;
    }

    .android-repay-plan-sheet__notice-icon {
      color: #0B45FF;
    }
  }
}

.android-repay-plan-sheet__state {
  padding: 56px 16px;
  color: #64748B;
  font-size: 26px;
  line-height: 36px;
  text-align: center;

  button {
    margin-top: 16px;
    border: 0;
    background: transparent;
    color: #3155A5;
    font: inherit;
  }
}

.android-repay-plan-sheet__items {
  display: grid;
  gap: 16px;
  margin-top: 24px;
}

.android-repay-plan-item {
  width: 100%;
  box-sizing: border-box;
  padding: 20px;
  border: 2px solid #E5EAF3;
  border-radius: 16px;
}

.android-repay-plan-item__top,
.android-repay-plan-item__row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.android-repay-plan-item__row > span {
  min-width: 0;
  flex: 1 1 auto;
}

.android-repay-plan-item__row > strong,
.android-repay-plan-item__status {
  flex: 0 0 136px;
  justify-content: flex-end;
  text-align: right;
}

.android-repay-plan-item__period {
  padding: 6px 14px;
  border-radius: 14px;
  color: #0B45FF !important;
  background: #EAF3FF !important;
  font-weight: 400 !important;
}

.android-repay-plan-item__amount {
  color: #0B45FF !important;
  font-weight: 400 !important;
}

.android-repay-plan-item__date {
  color: #0B1440 !important;
  font-weight: 400 !important;
}

.android-repay-plan-item__top {
  align-items: center;
  font-size: 26px;
  line-height: 36px;

  > strong {
    padding: 6px 14px;
    border-radius: 14px;
    color: #3155A5;
    background: #EAF3FF;
    font-weight: 700;
  }
}

.android-repay-plan-item__row {
  margin-top: 12px;
  color: #64748B;
  font-size: 24px;
  line-height: 32px;

  strong {
    color: #0B45FF;
    font-weight: 400;
  }
}

.android-repay-plan-item__status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  font-size: 22px;
  line-height: 30px;

  .van-icon,
  .android-order-icon {
    width: 28px;
    height: 28px;
    flex: 0 0 28px;
    font-size: 28px;
  }

  &--paid { color: #15803D; }
  &--overdue { color: #DC2626; }
  &--unpaid { color: #F97316; }
  &--upcoming { color: #64748B; }
}

.android-order-price-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  background: #EEF5FF;

  > .van-icon,
  > .android-order-icon {
    flex: 0 0 18px;
    margin-top: 1px;
    color: #3155A5;
    font-size: 18px;
  }

  > div {
    min-width: 0;
    flex: 1;
  }

  strong,
  span {
    display: block;
  }

  strong {
    color: #172554;
    font-size: 13px;
    line-height: 18px;
    font-weight: 600;
  }

  span {
    margin-top: 4px;
    color: #64748B;
    font-size: 12px;
    line-height: 16px;
  }

  &--warning {
    background: #FFF9F0;

    > .van-icon,
    > .android-order-icon {
      color: #F59E0B;
    }
  }
}

.android-order-notes-card {
  padding: 8px;
}

.android-order-notes-card__title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #172554;
  font-size: 15px;
  line-height: 22px;
  font-weight: 600;

  .van-icon,
  .android-order-icon {
    flex: 0 0 18px;
    color: #3155A5;
    font-size: 18px;
  }
}

.android-order-notes-card__body {
  margin-top: 8px;
  color: #172554;
  font-size: 13px;
  line-height: 18px;
}

.android-order-note-list {
  display: grid;
  gap: 4px;
  margin: 4px 0 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: #172554;
    font-size: 12px;
    line-height: 16px;
  }
}

.android-order-note-bullet {
  flex: 0 0 5px;
  width: 5px;
  height: 5px;
  margin-top: 6px;
  border-radius: 50%;
  background: #3155A5;
}

.android-order-additional-card {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  color: #3155A5;
  font-size: 13px;
  line-height: 18px;

  span {
    min-width: 0;
    flex: 1;
  }
}

.android-order-bottom-cancel {
  display: block;
  width: 100%;
  height: 46px;
  margin-top: 8px;
  border: 1px solid #DC2626;
  border-radius: 8px;
  box-sizing: border-box;
  color: #DC2626;
  background: #fff;
  font-size: 13px;
  line-height: 44px;
  font-weight: 400;
  text-align: center;
}

.android-order-footer {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 8;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
  border-top: 1px solid #E5EAF3;
  background: var(--bg-color);

  &__button {
    flex: 1 1 0;
    min-width: 0;
    height: 46px;
    padding: 0 8px;
    border: 1px solid #3155A5;
    border-radius: 8px;
    background: #fff;
    color: #3155A5;
    font-size: 13px;
    line-height: 44px;
    font-weight: 400;
    white-space: nowrap;

    .van-icon {
      margin-right: 4px;
      vertical-align: -2px;
    }

    &--primary {
      border-color: var(--primary-color);
      color: #fff;
      background: var(--primary-color);
    }

    &--danger {
      border-color: #DC2626;
      color: #DC2626;
    }

    &:disabled {
      opacity: .5;
    }
  }

  &__row {
    display: flex;
    flex: 1 0 100%;
    gap: 8px;
    width: 100%;
  }

  &__button--full {
    flex: 1 0 100%;
    width: 100%;
  }

  &__button--support {
    flex: 0 0 106px;
    width: 106px;
    padding: 0;
    border-color: #DDE5F2;

    img {
      display: block;
      width: 40px;
      height: 40px;
      margin: auto;
    }
  }

  &__button--secondary {
    flex: 1 1 auto;
    border-color: #0B45FF;
    color: #fff;
    background: #0B45FF;

    &:active,
    &:focus {
      border-color: #0B45FF;
      color: #fff;
      background: #0B45FF;
    }
  }

  gap: 12px;

  &--payment {
    flex-direction: column;
  }

  &--payment &__button {
    font-size: 12px;
  }
}

@media (min-width: 600px) {
  .android-order-detail {
    max-width: 600px;
    margin: 0 auto;
  }

  .android-order-footer {
    left: 50%;
    width: 600px;
    transform: translateX(-50%);
  }
}
</style>
<style lang="less" scoped>
.order-detail {
  min-height: 100vh;
  padding-top: 88px;
  box-sizing: border-box;
  background: var(--bg-color);

  &__status {
    display: flex;
    flex-direction: column;
    align-items: center;

    &.status-orange { color: #FF8A00; background: rgba(255, 138, 0, 0.10); }
    &.status-blue   { color: #2563EB; background: rgba(37, 99, 235, 0.10); }
    &.status-green  { color: #22C55E; background: rgba(34, 197, 94, 0.10); }
    &.status-gray   { color: #64748B; background: rgba(100, 116, 139, 0.10); }
    &.status-purple { color: #8B5CF6; background: rgba(139, 92, 246, 0.10); }
    &.status-cyan   { color: #14B8A6; background: rgba(20, 184, 166, 0.10); }

    .status-badge {
      font-size: 32px;
      font-weight: 600;
      line-height: 1;
      padding: 40px 16px 30px 16px;
      width: 100%;
      text-align: center;
    }

    .order-detail__status-action {
      padding: 0 30px 20px;
      width: 100%;
      box-sizing: border-box;
    }

    .status-close-reason {
      width: calc(100% - 60px);
      margin: 0 30px 20px;
      padding: 22px 24px;
      box-sizing: border-box;
      border-radius: 12px;
      background: rgba(100, 116, 139, 0.10);
      text-align: center;
      color: #334155;

      &__label {
        font-size: 26px;
        line-height: 36px;
        color: rgba(51, 65, 85, 0.68);
        flex-shrink: 0;
        white-space: nowrap;
      }

      &__value {
        font-size: 26px;
        line-height: 36px;
        font-weight: 500;
        color: gray;
        word-break: break-word;
      }
    }

    &.status-orange .status-close-reason { background: rgba(255, 138, 0, 0.10); }
    &.status-blue .status-close-reason   { background: rgba(37, 99, 235, 0.10); }
    &.status-green .status-close-reason  { background: rgba(34, 197, 94, 0.10); }
    &.status-gray .status-close-reason   { background: rgba(100, 116, 139, 0.10); }
    &.status-purple .status-close-reason { background: rgba(139, 92, 246, 0.10); }
    &.status-cyan .status-close-reason   { background: rgba(20, 184, 166, 0.10); }

    .status-pay-btn {
      height: 72px;
      line-height: 72px;
      border-radius: 8px;
      font-size: 28px;
      font-weight: 500;
      text-align: center;
      color: #FFFFFF;
      background: #FF8A00;

      &--disabled {
        background: rgba(255, 138, 0, 0.45);
        pointer-events: none;
      }
    }

    &.status-orange .logistics-card { background: rgba(255, 138, 0, 0.10); }
    &.status-blue .logistics-card   { background: rgba(37, 99, 235, 0.10); }
    &.status-green .logistics-card  { background: rgba(34, 197, 94, 0.10); }
    &.status-gray .logistics-card   { background: rgba(100, 116, 139, 0.10); }
    &.status-purple .logistics-card { background: rgba(139, 92, 246, 0.10); }
    &.status-cyan .logistics-card   { background: rgba(20, 184, 166, 0.10); }
  }

  &__logistics {
    padding: 0 30px 20px 30px;
    width: 100%;
  }

  .logistics-card {
    padding: 24px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;

    &__left {
      display: flex;
      align-items: center;
      font-size: 24px;
      color: var(--primary-color);
    }

    &__icon {
      margin-right: 8px;
      font-size: 28px;
    }

    &__label {
      margin-right: 8px;
    }

    &__status {
      font-weight: 500;
    }

    &__arrow {
      font-size: 24px;
      color: var(--primary-color);
      flex-shrink: 0;
      margin-left: 12px;
      font-weight: 500;
    }
  }

  .additional-info-banner {
    display: flex;
    align-items: center;
    padding: 24px 20px;
    margin: 20px 30px 0;
    background: #eff6fd;
    border-radius: 16px;
    animation: info-banner-pulse 1s ease-in-out infinite;

    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #2563EB;
      width: 48px;
      flex-shrink: 0;
      font-size: 70px;
    }

    &__body {
      flex: 1;
      margin: 0 16px;
    }

    &__title {
      font-size: 28px;
      font-weight: 600;
      color: #2563EB;
      line-height: 38px;
    }

    &__desc {
      margin-top: 10px;
      font-size: 24px;
      color: #6B7280;
      line-height: 34px;
    }

    &__arrow {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #7b8ff9;
      flex-shrink: 0;
      font-size: 28px;
      font-weight: 500;
    }
  }

  &__address,
  &__contact {
    .btn-primary {
      margin: 0 30px;
    }
  }

  &__goods {
    background: #ffffff;
    padding: 30px 40px 58px 40px;
  }

  .repay-plan-entry {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 20px 30px 0;
    padding: 24px 30px;
    border-radius: 16px;
    background: #ffffff;
    color: var(--primary-color);

    &__title {
      font-size: 28px;
      line-height: 38px;
      font-weight: 500;
    }

    &__desc {
      margin-top: 6px;
      color: var(--text-color-gray);
      font-size: 24px;
      line-height: 32px;
    }

    .van-icon {
      font-size: 30px;
    }
  }

  &__info {
    background: #ffffff;
    padding: 53px 40px 38px 40px;
  }

  &__info2 {
    background: #ffffff;
    padding: 30px 40px;
  }

  &__notes {
    background: #ffffff;
    padding: 30px 40px;
  }

  &__goods-info-img {
    width: 160px;
    height: 160px;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 8px;
    border: 1px solid var(--border-color-base);
    overflow: hidden;
  }

  &__goods-info-name {
    width: 480px;
    font-weight: 500;
    font-size: 26px;
    line-height: 38px;
  }

  &__goods-info-sku {
    margin-top: 12px;
    font-size: 26px;
    color: #999999;
    line-height: 32px;
  }

  &__footer {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    box-sizing: border-box;
    padding: 30px 30px 50px 30px;
    background: var(--bg-color);
  }
}

.notes-title {
  font-size: 32px;
  line-height: 37px;
  font-weight: 500;
}

.notes-content {
  font-size: 26px;
  line-height: 30px;
  margin-top: 30px;
  color: var(--text-color-gray);
}

.address-info {
  padding: 30px 60px;
  background-color: #ffffff;

  &.next {
    background: #ffffff url(../../assets/img/next.png) right 30px center no-repeat;
    background-size: auto 24px;
  }

  .icon-friends {
    font-size: 32px;
    color: var(--primary-color);
  }

  &__img {
    width: 32px;
    height: 32px;
  }

  &__text {
    margin-left: 20px;
    font-size: 26px;
    line-height: 38px;
    color: var(--text-color-gray);

    .b-text {
      font-size: 28px;
      color: var(--text-color-black);
      margin-right: 20px;
      font-weight: 500;
    }
  }
}

.contact-info {
  padding: 30px 60px;
  background-color: #ffffff;

  &.next {
    background: #ffffff url(../../assets/img/next.png) right 30px center no-repeat;
    background-size: auto 24px;
  }

  .icon-friends {
    font-size: 32px;
    color: var(--primary-color);
  }

  &__text {
    margin-left: 20px;
    font-size: 26px;
    line-height: 38px;
    color: var(--text-color-gray);
  }

  .contact-item {
    word-break: break-all;

    span {
      display: block;
    }
  }

  &__text-title {
    font-size: 32px;
    color: var(--text-color-black);
  }
}

.goods-plan {
  margin-top: 27px;

  &__left,
  &__right {
    width: 280px;
    box-shadow: 0px 8px 30px 0px rgba(0, 0, 0, 0.08);
    border-radius: var(--border-radius-base);
    text-align: center;
    padding-bottom: 24px;

    img {
      width: 32px;
      height: 32px;
      display: block;
      margin: 32px auto 0 auto;
    }

    .desc {
      margin-top: 20px;
      font-size: 26px;
      color: #999999;
      line-height: 32px;
    }

    .date {
      margin-top: 13px;
      font-size: 28px;
      color: var(--primary-color);
      line-height: 32px;
      font-weight: 500;
    }
  }

  &__tips {
    margin-top: 12px;
    font-size: 20px;
    color: var(--text-color-gray);
    line-height: 32px;
    text-align: center;
  }

  &__arrow {
    width: 0;
    height: 0;
    border-top: 20px solid transparent;
    /* 上边框透明 */
    border-bottom: 20px solid transparent;
    /* 下边框透明 */
    border-left: 20px solid #999999;
    /* 左边框为黑色，形成向右的箭头 */
  }
}

.text-total {
  font-size: 42px;
  line-height: 48px;
}

.org {
  color: var(--red-color);

}

.cod-amount-title {
  color: #E53935;
  font-size: 34px;
  line-height: 42px;
}

.cod-reminder-card {
  margin: 24px 30px 0;
  padding: 24px;
  border: 2px solid #FFB36B;
  border-radius: 12px;
  background: #FFF4EC;

  &__title {
    font-size: 28px;
    line-height: 36px;
    font-weight: 600;
    color: #FF7A00;
  }

  &__desc {
    margin-top: 12px;
    font-size: 24px;
    line-height: 34px;
    color: #6B7280;
  }

}

.text-left {
  font-size: 26px;
  color: var(--text-color-gray);
  line-height: 32px;
}

.text-right {
  font-size: 26px;
  color: var(--text-color-black);
  line-height: 32px;
}

.text-right--multiline {
  max-width: 420px;
  text-align: right;
  white-space: normal;
  word-break: break-word;
}


.agreement {
  padding-bottom: 20px;
  font-size: 26px;
  line-height: 30px;
  color: var(--text-color-gray);

  > span {
    color: var(--link-color);
  }
}

::v-deep .van-popup.cancel-popup-wrap {
  background: none;
}

.cancel-popup {
  width: 750px;
  background: #FFFFFF;
  border-radius: 50px 50px 0px 0px;
  filter: blur(0px);
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 85vh;

  &__close {
    position: absolute;
    right: 22px;
    top: 28px;
    width: 40px;
    height: 40px;
    z-index: 1;

    img {
      margin: 8px auto;
      display: block;
      width: 24px;
      height: 24px;
    }
  }

  &__title {
    font-size: 42px;
    line-height: 48px;
    font-weight: 600;
    text-align: center;
    padding: 30px 30px 20px;
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 0 30px;
  }

  &__desc-card,
  &__summary-card,
  &__reason-card {
    background: #F8FAFE;
    border: 1px solid #E5E7EB;
    border-radius: 18px;
    padding: 24px;
  }

  &__desc-card {
    color: #6B7280;
    font-size: 28px;
    line-height: 40px;
  }

  &__summary-card,
  &__reason-card,
  &__cod-notice {
    margin-top: 24px;
  }

  &__product {
    display: flex;
    align-items: center;
  }

  &__product-img {
    width: 120px;
    height: 120px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 8px;
    flex-shrink: 0;
  }

  &__product-info {
    flex: 1;
    min-width: 0;
    margin-left: 20px;
  }

  &__product-name {
    font-size: 26px;
    line-height: 36px;
    color: var(--text-color-black);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__product-sku {
    margin-top: 12px;
    font-size: 24px;
    line-height: 34px;
    color: #6B7280;
  }

  &__divider {
    margin-top: 24px;
    display: flex;
    align-items: center;
    color: #6B7280;

    span {
      height: 1px;
      flex: 1;
      background: #E5E7EB;
    }

    .van-icon {
      margin: 0 10px;
      font-size: 32px;
    }
  }

  &__amount-block {
    margin-top: 24px;
  }

  &__amount-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #0F172A;
    font-size: 28px;
    line-height: 38px;
  }

  &__unpaid-pill {
    flex-shrink: 0;
    margin-left: 16px;
    padding: 6px 18px;
    border-radius: 999px;
    background: #FFF7ED;
    color: #F59E0B;
    font-size: 22px;
    line-height: 28px;
    border: 1px solid rgba(245, 158, 11, 0.22);
  }

  &__amount-value {
    margin-top: 10px;
    color: #337DFF;
    font-size: 34px;
    line-height: 44px;
  }

  &__section-title {
    font-size: 30px;
    line-height: 40px;
    color: #0F172A;
  }

  &__cod-notice {
    display: flex;
    align-items: flex-start;
    padding: 24px;
    border-radius: 18px;
    background: #FFF7ED;
    border: 1px solid rgba(245, 158, 11, 0.22);
  }

  &__cod-icon {
    width: 48px;
    height: 48px;
    border-radius: 16px;
    background: #FFFFFF;
    color: #F59E0B;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 30px;
  }

  &__cod-copy {
    flex: 1;
    min-width: 0;
    margin-left: 16px;
  }

  &__cod-title {
    color: #0F172A;
    font-size: 28px;
    line-height: 38px;
  }

  &__cod-desc {
    margin-top: 8px;
    color: #6B7280;
    font-size: 24px;
    line-height: 34px;
  }

  &__footer {
    display: flex;
    gap: 20px;
    padding: 30px;
    background: #FFFFFF;
  }

  &__footer-btn {
    flex: 1;
    height: 88px;
    border-radius: 24px;
    font-size: 30px;
    line-height: 86px;
    text-align: center;
    border: 2px solid transparent;
    background: #FFFFFF;

    &--secondary {
      border-color: #337DFF;
      color: #337DFF;
    }

    &--primary {
      border-color: #337DFF;
      background: #337DFF;
      color: #FFFFFF;
    }
  }

  &__reason {
    margin-top: 18px;
    border: 1px solid var(--border-color-base);
    border-radius: 12px;
    overflow: hidden;
    background: #FFFFFF;

    ::v-deep .van-field__control {
      font-size: 36px;
      padding: 10px 0;
      line-height: 48px;
    }
    ::v-deep .van-field__word-limit {
      font-size: 24px;
    }
  }

  &__refund {
    margin-top: 24px;
    padding: 20px;
    background: #F8FAFE;
    border-radius: 12px;

    ::v-deep .van-field__control {
      font-size: 36px;
      padding: 10px 0;
      line-height: 48px;
    }
  }

  &__refund-notice {
    display: flex;
    align-items: flex-start;
    margin: 18px 0 20px;
    padding: 20px;
    border-radius: 14px;
    background: rgba(51, 125, 255, 0.08);
  }

  &__refund-icon {
    flex-shrink: 0;
    margin-right: 14px;
    color: #337DFF;
    font-size: 28px;
    line-height: 34px;
  }

  &__refund-text {
    flex: 1;
    min-width: 0;
    color: #0F172A;
    font-size: 24px;
    line-height: 34px;
  }

  &__radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    padding-top: 8px;

    ::v-deep .van-radio {
      margin-bottom: 8px;
    }

    ::v-deep .van-radio__label {
      font-size: 28px;
    }

    ::v-deep .van-radio__icon {
      font-size: 32px;
    }
  }

  &__label {
    font-size: 28px;
    font-weight: 500;
    color: var(--text-color-black);
    margin-bottom: 12px;
  }

  &__field {
    margin-top: 20px;
  }

  &__selector {
    border: 1px solid var(--border-color-base);
    border-radius: var(--border-radius-base);
    padding: 20px;
    font-size: 28px;
    color: var(--text-color-gray);
    position: relative;
    background: #fff;

    &::after {
      position: absolute;
      content: '';
      width: 24px;
      height: 24px;
      right: 20px;
      top: 50%;
      background: #ffffff url(~@/assets/img/next.png) no-repeat right center/auto 24px;
      transform: translateY(-50%) rotate(90deg);
    }
  }
}

.cancel-btn-wrap {
  padding: 30px;
  background: #ffffff;
}

.cancel-btn {
  height: 88px;
  border-radius: 16px;
  border: 2px solid #FF4D4F;
  color: #FF4D4F;
  background: #fff;
  font-size: 30px;
  line-height: 86px;
  text-align: center;
  font-weight: 500;
}

.order-cancel-card {
  display: flex;
  padding: 24px;
  margin: 20px 24px 0;
  border-radius: 16px;
  border: 1px solid;
  margin-bottom: 20px;

  &__icon {
    font-size: 28px;
    width: 36px;
    flex-shrink: 0;
    margin-top: 4px;
  }

  &__body {
    flex: 1;
    margin-left: 16px;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__title {
    font-size: 28px;
    font-weight: 500;
  }

  &__time {
    font-size: 22px;
    color: #999;
    flex-shrink: 0;
    margin-left: 12px;
  }

  &__subtitle {
    margin-top: 10px;
    font-size: 26px;
  }

  &__desc {
    margin-top: 10px;
    font-size: 24px;
  }

  &__reason {
    display: flex;
    margin-top: 14px;
    padding: 18px 20px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.72);
    color: #334155;
    align-items: center;
    gap: 5px;
  }

  &__reason-label {
    font-size: 26px;
    line-height: 36px;
    color: rgba(51, 65, 85, 0.68);
    flex-shrink: 0;
    white-space: nowrap;
  }

  &__reason-value {
    font-size: 26px;
    line-height: 36px;
    font-weight: 500;
    color: gray;
    word-break: break-word;
  }

  &__action {
    margin-top: 14px;
    text-align: right;
    font-size: 24px;
    color: #FF4D4F;
  }

  &--pending {
    border-color: #bbe5c7;
    background: #f6fcf6;

    .order-cancel-card__icon,
    .order-cancel-card__title,
    .order-cancel-card__subtitle {
      color: #00C853;
    }
    .order-cancel-card__desc {
      color: #6b7280;
    }
  }

  &--rejected {
    border-color: rgba(255, 77, 79, 0.4);
    background: rgba(255, 77, 79, 0.1);

    .order-cancel-card__icon,
    .order-cancel-card__title {
      color: #ff4d4f;
    }
    .order-cancel-card__desc {
      color: #6b7280;
    }
  }
}

@keyframes info-banner-pulse {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.03); }
}
</style>

<style lang="less" scoped>
/* pxtorem uses a 75px design width; Android dp/sp tokens therefore use a 2x source value. */
.order-detail {
  padding-top: 92px;

  ::v-deep .van-nav-bar {
    height: 92px;
  }

  ::v-deep .van-nav-bar__title {
    font-size: 30px;
  }

  ::v-deep .van-nav-bar .van-icon {
    font-size: 40px;
  }
}

.android-order-detail {
  min-height: calc(100vh - 92px);
  padding: 16px 16px calc(1.88rem + env(safe-area-inset-bottom));
  font-size: 26px;
  line-height: 36px;
}

.android-order-status-card,
.android-order-cancel-card,
.android-order-section,
.android-order-note-card,
.android-order-additional-card {
  margin-bottom: 16px;
  border-width: 2px;
  border-radius: 20px;
}

.android-order-status-card {
  padding: 16px;

  &__top { gap: 16px; align-items: center; }
  /* 顶部状态主图标需要和标题视觉高度匹配，不能按说明行小图标处理。 */
  &__icon { flex-basis: 56px; width: 56px; height: 56px; font-size: 34px; }
  &__title { font-size: 30px; line-height: 44px; font-weight: 600; }
  &__body { margin-top: 16px; padding: 16px 0 0; border-top-width: 2px; }
  &__description { font-size: 24px; line-height: 32px; }
  &__cod-pay { height: 52px; margin-top: 12px; padding: 0 16px; border-width: 2px; border-radius: 8px; font-size: 22px; line-height: 32px; }
  &__store-pay { height: 52px; margin-top: 12px; padding: 0 16px; border-width: 2px; border-radius: 8px; font-size: 22px; line-height: 32px; }
  &__cod-paid { min-height: 52px; margin-top: 16px; padding: 10px 32px; border-width: 2px; border-radius: 8px; gap: 8px; font-size: 24px; line-height: 32px; }
  &__list-item { gap: 16px; font-size: 24px; line-height: 32px; }
  &__bullet { flex-basis: 8px; width: 8px; height: 8px; margin-top: 12px; }
  &__badge { padding: 6px 14px; font-size: 24px; line-height: 32px; }
  &__meta,
  &__deadline { margin-top: 16px; font-size: 24px; line-height: 32px; }
  &__pending { margin-top: 16px; padding: 16px 0 0; border-top-width: 2px; }
  &__pending-desc { font-size: 26px; line-height: 36px; }
  &__pending-countdown { gap: 8px; margin-top: 16px; font-size: 32px; line-height: 40px; }
  &__pending-countdown .van-icon { font-size: 30px; }
  &__time { gap: 8px; margin-top: 16px; font-size: 26px; line-height: 34px; }
  &__time .van-icon { flex: 0 0 30px; width: 30px; height: 34px; font-size: 30px; line-height: 34px; }
  &__time span { line-height: 34px; }
  &__link { margin-top: 16px; padding-top: 16px; font-size: 26px; }
}

.android-order-status-card.status-purple {
  color: #6D3DB8;
  border-color: #DCCCF5;
  background: #F7F0FF;

  .android-order-status-card__icon { color: #6D3DB8; background: #EADCFB; }
  .android-order-status-card__badge { color: #6D3DB8; background: #FFFFFF; }
}

.android-order-action {
  height: 92px;
  margin-top: 16px;
  border-radius: 16px;
  font-size: 26px;
  line-height: 88px;
}

.android-order-cancel-card { gap: 16px; padding: 16px; }
.android-order-cancel-card__icon { flex-basis: 48px; width: 48px; height: 48px; border-radius: 16px; font-size: 36px; }
.android-order-cancel-card__title { font-size: 30px; line-height: 44px; }
.android-order-cancel-card__inline-status { font-size: 26px; line-height: 36px; }
.android-order-cancel-card__list { margin-top: 12px; }
.android-order-cancel-card__list-item { gap: 12px; margin-top: 8px; font-size: 24px; line-height: 32px; }
.android-order-cancel-card__bullet { flex-basis: 10px; width: 10px; height: 10px; margin-top: 10px; }
.android-order-cancel-card__action { margin-top: 16px; }
.android-order-text-button,
.android-order-copy { font-size: 24px; line-height: 32px; }
.android-order-text-button { margin-top: 16px; }
.android-order-cancel-card__action .android-order-text-button { min-width: 200px; height: 72px; padding: 0 28px; border-radius: 16px; font-size: 26px; line-height: 72px; }

.android-order-section {
  &__header { min-height: 76px; padding: 16px; }
  &__heading { gap: 16px; font-size: 30px; line-height: 44px; font-weight: 600; }
  &__heading .van-icon { flex-basis: 36px; font-size: 36px; }
  &__heading ::v-deep .android-order-icon {
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    padding: 4px;
    box-sizing: border-box;
    color: #102BDB;
  }
  &__header > .van-icon { font-size: 32px; }
  &__body { padding: 16px; border-top-width: 2px; }
  &__summary { padding: 16px 16px 16px 64px; border-top: 2px solid #E5EAF3; color: #64748B; font-size: 24px; line-height: 32px; }
  &__summary + &__body { padding-top: 0; border-top: 0; }
}

.android-order-address__name,
.android-order-contact,
.android-order-product__name,
.android-order-price-row { font-size: 26px; line-height: 36px; }
.android-order-address__phone,
.android-order-address__detail { font-size: 26px; line-height: 36px; }
.android-order-address { padding-left: 48px; }
.android-order-muted { margin-top: 8px; font-size: 24px; line-height: 32px; }
  .android-order-contact { gap: 16px; padding: 8px 0 0 48px; }
  .android-order-contact__name,
  .android-order-contact__relationship { font-size: 26px; line-height: 36px; }
.android-order-contact__identity small { font-size: 24px; }
.android-order-contact__phone { max-width: none; margin-top: 8px; font-size: 26px; line-height: 36px; }
.android-order-contact__divider { margin-top: 20px; }
.android-order-product { gap: 16px; }
.android-order-product__name { white-space: nowrap; }
.android-order-product__image { flex-basis: 144px; width: 144px; height: 144px; border-width: 0; border-radius: 16px; }
.android-order-number { gap: 16px; margin-top: 16px; padding-top: 0; border-top: 0; font-size: 24px; line-height: 32px; }
.android-order-number > span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.android-order-product { margin-top: 16px; }
.android-order-copy {
  width: 48px;
  height: 48px;
  color: #0B45FF;
}
.android-order-copy ::v-deep .android-order-icon {
  width: 36px;
  height: 36px;
  padding: 4px;
  box-sizing: border-box;
  color: #0B45FF;
}
.android-order-date-grid { gap: 16px; }
.android-order-date-block { padding: 16px; border-width: 2px; border-radius: 16px; text-align: center; }
.android-order-date-block span { font-size: 24px; line-height: 32px; }
.android-order-date-block small { margin-top: 4px; font-size: 24px; line-height: 32px; }
.android-order-date-block strong { margin-top: 8px; font-size: 26px; line-height: 36px; }
.android-order-info-note { gap: 8px; margin-top: 16px; font-size: 24px; line-height: 32px; }
.android-order-note-card { padding: 16px; }
.android-order-note-card__title { font-size: 30px; line-height: 44px; }
.android-order-note-card__text { margin-top: 8px; font-size: 24px; line-height: 32px; }
.android-order-note-card--cod-shipping-fee { display: flex; gap: 14px; padding: 20px 24px 24px; border-color: #FFD79A; }
.android-order-note-card--cod-shipping-fee .android-order-note-card__icon { flex-basis: 52px; width: 52px; height: 52px; margin-top: 4px; border-radius: 12px; font-size: 0; line-height: 1; }
.android-order-note-card--cod-shipping-fee .android-order-note-card__icon::before { width: 28px; height: 28px; }
.android-order-note-card--cod-shipping-fee .android-order-note-card__icon::after { font-size: 20px; line-height: 28px; }
.android-order-note-card--cod-shipping-fee .android-order-note-card__content { min-width: 0; flex: 1; }
.android-order-note-card--cod-shipping-fee .android-order-note-card__title { line-height: 40px; }
.android-order-note-card--cod-shipping-fee .android-order-note-card__text { margin-top: 12px; line-height: 36px; }
.android-order-note-card--cod-reminder { gap: 14px; padding: 20px 32px 24px; }
.android-order-note-card--cod-reminder .android-order-note-card__icon { flex-basis: 52px; width: 52px; height: 52px; margin-top: 4px; border-radius: 12px; font-size: 0; line-height: 1; }
.android-order-note-card--cod-reminder .android-order-note-card__icon::before { width: 28px; height: 28px; }
.android-order-note-card--cod-reminder .android-order-note-card__icon::after { font-size: 20px; line-height: 28px; }
.android-order-note-card--cod-reminder .android-order-note-card__content { max-width: none; }
.android-order-note-card--cod-reminder .android-order-note-card__title { line-height: 40px; }
.android-order-note-card--cod-reminder .android-order-note-card__text { margin-top: 8px; text-align: left; }
.android-order-note-card--cod-reminder .android-order-note-card__cod-row { gap: 8px; margin-top: 8px; }
.android-order-note-card--cod-reminder .android-order-note-card__currency { font-size: 32px; line-height: 40px; }
.android-order-note-card--cod-reminder .android-order-note-card__cod-desc { max-width: none; font-size: 24px; line-height: 32px; text-align: left; }
.android-order-note-card--store-reminder { gap: 14px; padding: 20px 32px 24px; }
.android-order-note-card--store-reminder .android-order-note-card__icon { flex-basis: 52px; width: 52px; height: 52px; margin-top: 4px; border-radius: 12px; font-size: 0; line-height: 1; }
.android-order-note-card--store-reminder .android-order-note-card__icon::before { width: 28px; height: 28px; }
.android-order-note-card--store-reminder .android-order-note-card__icon::after { font-size: 20px; line-height: 28px; }
.android-order-note-card--store-reminder .android-order-note-card__content { max-width: none; }
.android-order-note-card--store-reminder .android-order-note-card__title { line-height: 40px; }
.android-order-note-card--store-reminder .android-order-note-card__text { margin-top: 8px; font-size: 24px; line-height: 32px; text-align: left; }
.android-order-price-row { gap: 16px; padding: 8px 0; }
.android-order-price-row--cod-total strong { font-size: 30px; line-height: 40px; }
.android-order-divider { height: 2px; margin: 8px 0; }
.android-order-link-row { margin-top: 16px; padding-top: 16px; border-top-width: 2px; font-size: 26px; line-height: 36px; }
.android-order-link-row__icon { flex-basis: 36px; font-size: 36px; }
.android-order-price-notices { gap: 16px; margin-top: 16px; }
.android-order-price-notice { gap: 16px; padding: 16px; border-radius: 16px; }
  .android-order-price-notice > .van-icon { flex-basis: 36px; margin-top: 2px; font-size: 36px; }
  .android-order-price-notice > ::v-deep .android-order-icon {
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    margin-top: 2px;
    color: #0B45FF;
  }
  .android-order-price-notice--warning > ::v-deep .android-order-icon { color: #F59E0B; }
.android-order-price-notice strong { font-size: 26px; line-height: 36px; }
.android-order-price-notice span { margin-top: 8px; font-size: 24px; line-height: 32px; }
.android-repay-plan-sheet { padding: 16px 28px calc(16px + env(safe-area-inset-bottom)); }
.android-repay-plan-sheet__header { min-height: 88px; border-bottom-width: 2px; }
.android-repay-plan-sheet__title { font-size: 32px; line-height: 44px; }
.android-repay-plan-sheet__header > .van-icon { font-size: 40px; }
.android-repay-plan-sheet__return { margin-top: 16px; font-size: 26px; line-height: 36px; }
.android-repay-plan-sheet__body { max-height: 68vh; padding-top: 24px; }
.android-repay-plan-sheet__notice { gap: 16px; padding: 20px; border-radius: 16px; }
.android-repay-plan-sheet__notice-icon { flex-basis: 48px; width: 48px; height: 48px; margin-top: 2px; border-radius: 8px; }
.android-repay-plan-sheet__notice-icon .van-icon { font-size: 30px; }
.android-repay-plan-sheet__notice > div > strong { font-size: 26px; line-height: 36px; }
.android-repay-plan-sheet__notice > div > span { margin-top: 4px; font-size: 24px; line-height: 32px; }
.android-repay-plan-sheet__state { padding: 56px 16px; font-size: 26px; line-height: 36px; }
.android-repay-plan-sheet__state button { margin-top: 16px; }
.android-repay-plan-sheet__items { gap: 16px; margin-top: 24px; }
.android-repay-plan-item { padding: 20px; border-width: 2px; border-radius: 16px; }
.android-repay-plan-item__top { font-size: 26px; line-height: 36px; }
.android-repay-plan-item__row { margin-top: 12px; font-size: 24px; line-height: 32px; }
.android-repay-plan-item__status { gap: 8px; padding: 0; font-size: 22px; line-height: 30px; }
.android-repay-plan-item__status .van-icon,
.android-repay-plan-item__status .android-order-icon { width: 28px; height: 28px; flex: 0 0 28px; font-size: 28px; }
.android-repay-plan-sheet__notice--secure { margin-top: 24px; }
.android-order-notes-card { padding: 16px; }
.android-order-notes-card__title { gap: 16px; font-size: 30px; line-height: 44px; }
  .android-order-notes-card__title .van-icon { flex-basis: 36px; font-size: 36px; }
  .android-order-notes-card__title ::v-deep .android-order-icon {
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    padding: 4px;
    box-sizing: border-box;
    color: #102BDB;
  }
.android-order-notes-card__body { margin-top: 16px; font-size: 26px; line-height: 36px; }
.android-order-note-list { gap: 8px; margin-top: 8px; }
.android-order-note-list li { gap: 16px; font-size: 24px; line-height: 32px; }
.android-order-note-bullet { flex-basis: 10px; width: 10px; height: 10px; margin-top: 11px; background: #0B45FF; }
.android-order-additional-card { gap: 16px; padding: 16px; font-size: 26px; line-height: 36px; }
.android-order-bottom-cancel { height: 92px; margin-top: 16px; border-width: 2px; border-radius: 16px; font-size: 26px; line-height: 88px; }

.android-order-footer {
  gap: 24px;
  padding: 16px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  border-top-width: 2px;
}
.android-order-footer__row { gap: 24px; }
.android-order-footer__button { height: 92px; padding: 0 16px; border-width: 2px; border-radius: 16px; font-size: 26px; line-height: 88px; }
.android-order-footer--payment .android-order-footer__button { font-size: 24px; }
.android-order-footer__button--support { flex: 0 0 106px; width: 106px; padding: 0; }
.android-order-footer__button--support { border-color: #DDE5F2; }
.android-order-footer__button--support img { width: 40px; height: 40px; }
.android-order-footer__button--secondary { flex: 1 1 auto; border-color: #0B45FF; color: #fff; background: #0B45FF; }

.detail-pull-refresh::v-deep .van-pull-refresh__text {
  font-size: 24px;
  line-height: 34px;
  font-weight: 400;
  color: #64748B;
}
</style>
